// Tiny Town Online client: fixed 60 Hz simulation for your own character, interpolated everyone else,
// and the world drawn in the Inkwell style (render/ink*.ts).
import ink from './ink/ink.js';
import { sceneFor, type Scene } from './render/scene.js';
import { drawMob, drawMobLabel, mobTop } from './render/inkmobs.js';
import { drawBrawl, forgetBrawls } from './render/brawl.js';
import { Cinema, TITLE, INTRO } from './ui/cinema.js';
import { homeland } from '../../shared/homelands.js';
import { nationById, nationOfFaction } from '../../shared/nations.js';
import { Creator } from './ui/creator.js';
import { api } from './ui/account.js';
import { vesselsAt } from '../../shared/routes.js';
import { drawVessel } from './render/vessels.js';
import { People, paintLook, drawPerson as drawInkPerson, drawCheap, drawMount, drawAirship, personTop, personPicture, shadow, SEAT, SHIP_LIFT, DIR_TH } from './render/inkchar.js';
import { nameTag, placeTag, inkBubble, floatNum, hint as inkHint, battleRing, inkDialogue } from './render/inkui.js';
import { spellAt, drawStroke, type Stroke } from './render/inkbattle.js';
import { AIRSHIP } from '../../shared/mounts.js';
import { Maps, zoneOfName, type Dot, type Mark } from './ui/maps.js';
import { BattleScreen } from './ui/battle.js';
import { FormationUI } from './ui/formation.js';
import { zoneDef, PLACES, GROTTO_CHEST } from '../../shared/zones.js';
import { HIT, mobDef } from '../../shared/mobs.js';
import { conOf } from '../../shared/difficulty.js';
import { INPUT_REDUNDANCY, INPUT_SEND_EVERY, SUB, TICK_MS } from '../../shared/constants.js';
import { FORAGE, INTERACT_RANGE_PX } from '../../shared/world.js';
import { factionById } from '../../shared/factions.js';
import { applyLayout, getMap } from '../../shared/map.js';
import { RoomEditor } from './ui/roomedit.js';
import { drawGardenGround, gardenItems, gardenTarget } from './render/garden.js';
import { ANIMALS, CROPS, FED_MS, GARDEN_SHOP, animalReady, cropDef, growth, type GardenOp, type GardenView } from '../../shared/garden.js';
import type { Furniture } from '../../shared/interiors.js';
import type { Look } from '../../shared/look.js';
import type { ClientMsg, ServerMsg } from '../../shared/protocol.js';
import { animOf, Anim, Act, ATTACK_TICKS, DIR_NAMES, IN } from '../../shared/sim.js';
import { Follow, type FollowTarget } from './game/follow.js';
import { Layout } from './ui/layout.js';
import type { MountsHost } from './ui/mounts.js';
import { DOCK_TILE } from '../../shared/mounts.js';
import { questDef, goalNeed } from '../../shared/quests.js';
import type { KitLook } from '../../shared/kit.js';
import { SocialUI } from './ui/social.js';
import { abilityDef, jobDef, jobSkill, JOB_MAX_LEVEL, JOB_UNLOCK_LEVEL, ROLE_COLOR, ROLE_OF, type JobId } from '../../shared/jobs.js';
import { Connection } from './net/connection.js';
import { Predictor } from './game/prediction.js';
import { RemoteWorld, type Remote } from './game/world.js';
import { Input } from './game/input.js';
import { advance, makeNpcs, nearbyNpc, scheduleNpcs, startTalk, updateNpcs, walkNpcs, dirToward, politeGoodbye, npcPose, LIFE, type Dialogue, type Npc } from './game/npcs.js';
import { makeCritters, stepCritters, critterItems, workFx, drawMoonGlade, type Critter } from './render/townlife.js';
import { Sky, type Light } from './render/sky.js';
import { buildRoom, drawWindows, roomThumb, type Room } from './render/rooms.js';
import type { Billboard } from './render/fpv.js';
import { SnapCamera } from './ui/snap.js';
import { DOORS, DOOR_RANGE_PX, closedText, doorPoint, doorPush as pushOf, interiorDef, shopOpen } from '../../shared/interiors.js';
import { recentlyRained, type Weather } from '../../shared/time.js';
import { SpriteCache } from './render/sprites.js';
import { ChatBox, DebugPanel, setStatus, toast } from './ui/hud.js';
import { Gate, type CharacterSummary } from './ui/account.js';
import { Bag } from './ui/bag.js';
import { PartyUI } from './ui/party.js';
import { DevPanel } from './ui/dev.js';
import { CharacterUI } from './ui/character.js';
import { ModHost } from './mods/host.js';
import { BUILTIN_MODS } from './mods/examples.js';
import { modsTab } from './ui/mods.js';
import { feedbackTab } from './ui/feedback.js';
import { itemDef, FOODS, EQUIP_SLOTS } from '../../shared/items.js';
import { levelForXp, xpForLevel } from '../../shared/skills.js';
import { SettingsUI, type Settings } from './ui/settings.js';
import { AudioEngine, renderPreview } from './audio/audio.js';
import { itemIcon } from './render/items.js';
import { ask, choiceKey, choiceOpen } from './ui/choice.js';
import { Tutorial } from './ui/tutorial.js';
import { LootRolls } from './ui/lootroll.js';
import { mentorFor } from '../../shared/mentors.js';
import { hordeDef } from '../../shared/horde.js';

let map = getMap('town');
const CROWD_NAMES = 12;
const TS = map.ts;
let MW = map.w, MH = map.h;
let room: Room | null = null;                       // inside a building: its drawing
let scene: Scene | null = null;                     // outdoors: the zone's ground, props and lights
const rooms = new Map<string, Room>();
const npcsByMap = new Map<string, ReturnType<typeof makeNpcs>>();
const sprites = new SpriteCache();
const input = new Input();
const chat = new ChatBox();
const debug = new DebugPanel();
let npcs = makeNpcs(map);
/** The town cat and the pups (per map). */
const crittersByMap = new Map<string, Critter[]>();
let critters: Critter[] = makeCritters(map); crittersByMap.set('town', critters);
npcsByMap.set('town', npcs);
/** Switch what we draw (and the walls prediction uses) to another zone's map. */
function setMap(id: string): void {
  if (map.id === id) return;
  map = getMap(id); MW = map.w; MH = map.h;
  pred?.setMap(map);
  const r = interiorDef(id);
  room = r ? (rooms.get(id) ?? (rooms.set(id, buildRoom(r)), rooms.get(id)!)) : null;
  scene = room ? null : sceneFor(id, map.data.collision, MW, MH);
  document.getElementById('rideBtn')?.classList.toggle('off', !map.mounts);
  mobFx.clear(); floaters.length = 0;
  npcs = npcsByMap.get(id) ?? (npcsByMap.set(id, makeNpcs(map)), npcsByMap.get(id)!);
  critters = crittersByMap.get(id) ?? (crittersByMap.set(id, makeCritters(map)), crittersByMap.get(id)!);
  scheduleNpcs(npcs, sky.hour(), 0, true);
  dlg = null;
}
scene = sceneFor('town', map.data.collision, MW, MH);
const sky = new Sky();
const clockEl = document.getElementById('clock')!;
// ---------- creatures and fighting: what the server tells us, plus floating numbers ----------
const mobFx = new Map<number, { hurtAt: number; deadAt: number; airborne?: boolean; landAt?: number }>();
let shakeUntil = 0, shakeMs = 1, shakeAmp = 0;
/** Creatures that just noticed you (a "!" pops over them). */
const alerts = new Map<number, number>();
const floaters: { x: number; y: number; text: string; color: string; big: boolean; t0: number }[] = [];
let myFood: { id: string; until: number } | null = null;
/** Which place's wayshrine you're bound to, and a flare playing at a shrine you just bound to. */
let myBind = 'town', shrineFlare = 0;
/** The meal buff on the minimap: what it is and how long is left. */
function showFood(): void {
  const tag = document.getElementById('foodTag')!, f = myFood ? FOODS[myFood.id] : null;
  const left = myFood ? Math.ceil((myFood.until - Date.now()) / 60000) : 0;
  tag.hidden = !f || left <= 0 || !pred; if (!f || left <= 0) return;
  const text = `${f.icon} ${f.buff} · ${left}m`; if (tag.textContent !== text) { tag.textContent = text; tag.title = f.desc; }
}
setInterval(showFood, 15000);
const tutor = new Tutorial();
/** A hidden grotto's shimmer close by (tile), and whether the grotto you're in is cleared. */
let shimmerAt: [number, number] | null = null, grottoCleared = false;
const lootRolls = new LootRolls();
lootRolls.onAnswer = (id, choice, to) => conn?.send({ type: 'roll', id, choice, to });
/** /find Name: who you're looking for, and where they are on this map (when they're here). */
let finding: { name: string; at: { x: number; y: number } | null; said: boolean; zone?: string } | null = null;
// ---------- Tab targeting out in the world ----------
/** Who you've picked with Tab (a creature or a person): a ring at their feet and a frame with who they are. */
let fieldTarget: number | null = null;
function setFieldTarget(id: number | null): void {
  fieldTarget = id; let box = document.getElementById('targetFrame');
  if (id == null) { if (box) box.hidden = true; return; }
  if (!box) { box = document.createElement('div'); box.id = 'targetFrame'; box.className = 'target-frame'; document.body.append(box); }
  box.hidden = false; showFieldTarget();
}
function showFieldTarget(): void {
  const box = document.getElementById('targetFrame'), r = fieldTarget != null ? world.remotes.get(fieldTarget) : undefined; if (!box || !r?.info) return;
  const i = r.info; box.replaceChildren();
  const name = document.createElement('b'); name.textContent = i.name; box.append(name);
  if (i.kind === 'mob') {
    const d = mobDef(i.mob), con = d ? conOf(d.level, myJobLv) : null, line = document.createElement('span');
    line.textContent = d ? `Lv ${d.level} · ${con!.name}${d.aggro ? ' · notices you' : ''}${d.link ? ' · comes with friends' : ''}${d.night ? ' · night creature' : ''}` : ''; if (con) line.style.color = con.color;
    const hp = document.createElement('i'), fill = document.createElement('em'); fill.style.width = `${Math.round(100 * (i.hp ?? 1) / (i.max ?? 1))}%`; hp.append(fill);
    box.append(line, hp);
  } else if (i.job) { const j = jobDef(i.job as JobId), line = document.createElement('span'); line.textContent = `${j.icon} ${j.name}${i.fallen ? ' · down' : ''}`; box.append(line); }
}
/** Tab: the next creature or person near you, in the order you set (nearest first, or the weakest creature). */
function cycleFieldTarget(d: number): void {
  if (!pred) return;
  const p = myPos(), near: { id: number; dist: number; hp: number }[] = [];
  for (const r of world.remotes.values()) {
    if (r.goneAt || !r.info || r.id === me.id || (r.info.kind === 'mob' && (r.info.hp ?? 1) <= 0)) continue;
    const ps = world.pose(r); if (!ps) continue;
    const dist = Math.hypot(ps.x - p.x, ps.y - p.y); if (dist > 170) continue;
    near.push({ id: r.id, dist: dist + (r.info.kind === 'mob' ? 0 : 1000), hp: r.info.kind === 'mob' ? (r.info.hp ?? 1) / (r.info.max ?? 1) : 2 });   // creatures first
  }
  if (!near.length) { setFieldTarget(null); return; }
  near.sort((a, b) => settings.s.tabOrder === 'low' ? a.hp - b.hp || a.dist - b.dist : a.dist - b.dist);
  const i = near.findIndex(n => n.id === fieldTarget);
  setFieldTarget(near[i < 0 ? 0 : (i + d + near.length) % near.length].id); audio.play('click');
}
addEventListener('keydown', e => {
  if (e.code !== 'Tab' && e.code !== 'Escape') return;
  if (battle.active || choiceOpen() || !pred || /INPUT|TEXTAREA|SELECT/.test((document.activeElement as HTMLElement)?.tagName || '')) return;
  if (e.code === 'Escape') { if (fieldTarget != null) { setFieldTarget(null); e.preventDefault(); } return; }
  e.preventDefault(); cycleFieldTarget(e.shiftKey ? -1 : 1);
});
setInterval(showFieldTarget, 500);
/** A horde challenge: which wave of how many, in a banner at the top. */
function showHorde(h: { name: string; wave: number; waves: number } | undefined): void {
  let tag = document.getElementById('hordeTag');
  if (!h) { if (tag) tag.hidden = true; return; }
  if (!tag) { tag = document.createElement('div'); tag.id = 'hordeTag'; tag.className = 'horde-tag'; document.body.append(tag); }
  tag.hidden = false; const t = `${h.name} · Wave ${h.wave} of ${h.waves}`;
  if (tag.textContent !== t) { tag.textContent = t; tag.classList.remove('pop'); void tag.offsetWidth; tag.classList.add('pop'); }
}
/** Fallen: until when you can wait for a Raise (ms; 0 = standing). Weakened: until when. */
let fallenUntil = 0, weakUntil = 0, fallenTimer = 0;
function showFallen(): void {
  const on = fallenUntil > Date.now(), box = document.getElementById('fallen')!;
  box.hidden = !on; document.body.classList.toggle('is-fallen', on);
  clearInterval(fallenTimer);
  if (!on) return;
  const tick = () => { const s = Math.max(0, Math.ceil((fallenUntil - Date.now()) / 1000)); document.getElementById('fallenTime')!.textContent = `Back to your wayshrine by yourself in ${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; if (s <= 0) { fallenUntil = 0; showFallen(); } };
  document.getElementById('fallenText')!.textContent = battle.active ? 'A Cleric (or a Phoenix Down) can bring you back. Your party fights on.' : 'A friend who knows Raise can bring you back. Or go back to your wayshrine (Weakened for a few minutes).';
  tick(); fallenTimer = window.setInterval(tick, 500);
}
function showWeak(): void {
  const tag = document.getElementById('weakTag')!, left = weakUntil - Date.now();
  tag.hidden = left <= 0;
  if (left > 0) setTimeout(showWeak, Math.min(left + 50, 30_000));
}
let myHp = 60, myMax = 60, myHurtAt = 0, myMp = 0, myMaxMp = 0, myJob: JobId = 'knight', myJobLv = 1, zoneNow = '';
const maps = new Maps();
const hpBar = document.getElementById('hpBar')!;
function showHp(): void {
  const zk = zoneDef(map.id.split('~')[0])?.kind, field = zk === 'field' || zk === 'arena';
  hpBar.hidden = !(field || myHp < myMax);
  const k = myHp / Math.max(1, myMax);
  (hpBar.firstElementChild as HTMLElement).style.width = `${Math.round(k * 100)}%`;
  hpBar.querySelector('em')!.textContent = `♥ ${myHp} / ${myMax}`;
  hpBar.classList.toggle('mid', k <= 0.5 && k > 0.25); hpBar.classList.toggle('low', k <= 0.25);
  const mpBar = document.getElementById('mpBar')!;
  mpBar.hidden = hpBar.hidden || !myMaxMp;
  (mpBar.firstElementChild as HTMLElement).style.width = `${Math.round(myMp / Math.max(1, myMaxMp) * 100)}%`;
  mpBar.querySelector('em')!.textContent = `✦ ${myMp} / ${myMaxMp}`;
  const tag = document.getElementById('jobTag')!, jd = jobDef(myJob);
  tag.hidden = !pred; tag.textContent = `${jd.icon} ${jd.name} ${myJobLv}`; tag.style.background = jd.color;
  showXp();
}
/** Your job's experience, always on show: how far through this level you are (FFXI's EXP bar). */
function showXp(): void {
  const bar = document.getElementById('xpBar')!; bar.hidden = !pred;
  if (!pred) return;
  const have = charUI.xp[jobSkill(myJob)] ?? 0, lv = levelForXp(have), lo = xpForLevel(lv), hi = xpForLevel(lv + 1), max = lv >= JOB_MAX_LEVEL;
  const into = have - lo, need = Math.max(1, hi - lo);
  (bar.firstElementChild as HTMLElement).style.width = max ? '100%' : `${Math.round(Math.min(1, into / need) * 100)}%`;
  bar.querySelector('em')!.textContent = max ? 'EXP max level' : `EXP ${into} / ${need}`;
  bar.title = max ? 'Your job is at its highest level.' : `${need - into} more experience to level ${lv + 1}.`;
}
/** Is this forage spot there right now? Night spots open after dark, rain spots after a shower. */
const nodeOpen = (n: typeof FORAGE[number]) => !n.when || (n.when === 'night' ? sky.night() : recentlyRained(n.map, sky.now(), sky.override));
const bag = new Bag();
const party = new PartyUI();
const follow = new Follow();
const followPill = document.getElementById('followPill')!;
follow.onChange = (name, why) => {
  followPill.hidden = !name;
  if (name) { followPill.replaceChildren(`Following ${name}`); const s = document.createElement('small'); s.textContent = 'move to stop'; followPill.append(s); chat.system(`You're following ${name}. Move to stop.`); }
  else chat.system(why === 'lost' ? 'You lost sight of who you were following.' : 'You stopped following.');
};
followPill.addEventListener('click', () => follow.stop());
party.following = () => follow.name;
party.onFollow = name => startFollow(name);
/** Follow someone by name ('' = your party leader). They must be someone you can see. */
function startFollow(name: string | null): void {
  if (name === null) return follow.stop();
  const who = name || party.leaderName();
  if (!who) { chat.system('Follow whom? Try /follow name, or join a party.'); return; }
  const r = [...world.remotes.values()].find(x => x.info?.kind === 'player' && x.info.name.toLowerCase() === who.toLowerCase() && !x.goneAt);
  if (!r) { chat.system(`${who} isn't nearby.`); return; }
  follow.start(r.info!.name);
}
function followTarget(): FollowTarget | null {
  if (!follow.name) return null;
  for (const r of world.remotes.values()) {
    if (r.goneAt || r.info?.kind !== 'player' || r.info.name !== follow.name) continue;
    const p = world.pose(r); if (!p) return null;
    return { x: p.x, y: p.y, mounted: !!p.m };
  }
  return null;
}
const dev = new DevPanel();
const charUI = new CharacterUI();
const mods = new ModHost(BUILTIN_MODS);
mods.print = (name, text) => chat.line('system', null, `[${name}] ${text}`);
const settings = new SettingsUI();
const audio = new AudioEngine();
sky.onThunder = () => audio.thunder();

// ---------- the sightseeing camera ----------
let groundFor = '';
const snap = new SnapCamera({
  start: () => {
    if (!pred) return null;
    if (room) { toast('The camera works outdoors for now.', 'info'); return null; }
    if (!scene) return null;
    if (groundFor !== scene.id) { groundFor = scene.id; snap.fp.setGround(scene.flat()); }
    return { x: pred.state.x / SUB, y: pred.state.y / SUB, dir: pred.state.dir, place: zoneName };
  },
  billboards: fp => {
    const now = performance.now(), out: Billboard[] = [];
    for (const pr of scene?.props ?? []) if (pr.spr && !pr.flat) {
      const sp = pr.spr, px = sp.c.width / sp.w;   // picture pixels per ink unit
      out.push({ x: pr.x, y: pr.y, img: sp.c, ax: sp.ox * px, scale: px * 3, lift: -(sp.h - sp.oy) / 3, kind: 'prop' });
    }
    for (const n of FORAGE) if (n.map === map.id && nodeOpen(n) && (nodeReady.get(n.id) ?? 0) <= now) out.push({ x: n.tile[0] * TS + 8, y: n.tile[1] * TS + 12, img: itemIcon(n.item), scale: 8, kind: 'prop' });
    // people turn to face the lens as they would on screen: their facing is relative to where the camera looks
    const picture = (key: number | string, c: { th: number }) => { const th = c.th; c.th = th - fp.cam.yaw - Math.PI / 2; const pic = personPicture(key, c, now); c.th = th; return pic; };
    npcs.forEach((n, i) => {
      if (n.alpha <= 0.05) return;
      const pz = npcPose(n, false), c = people.step(`n${i}`, n.look, { name: n.name, npc: true, tool: pz.tool, work: pz.work }, n.x, n.y, Math.max(0, ['down', 'right', 'up', 'left'].indexOf(n.dir)), false, 0, now);
      out.push({ x: n.x, y: n.y, ...picture(`n${i}`, c), alpha: n.alpha, kind: 'person', name: n.name });
    });
    for (const r of world.remotes.values()) {
      const ps = world.pose(r); if (!ps || !r.info || r.info.kind === 'mob') continue;
      const c = people.step(r.id, r.info.look, { job: r.info.job }, ps.x, ps.y, ps.dir, ps.anim === Anim.Attack, 0, now);
      out.push({ x: ps.x, y: ps.y, ...picture(r.id, c), kind: 'person', name: r.info.name });
    }
    return out;
  },
  view: () => {
    const lights = (scene?.lamps ?? []).map(L => ({ x: L.x, y: L.y + 29, r: L.r }));
    return { hour: sky.hour(), ambient: sky.ambient(), darkness: sky.darkness(), rain: Math.min(1, sky.amount('rain') + sky.amount('storm')), fog: sky.amount('fog'),
      cloudy: Math.min(1, sky.amount('cloudy') + sky.amount('rain') + sky.amount('storm')), t: performance.now() / 1000, lights };
  },
  timeLabel: () => sky.label().replace(/^\S+ /, ''),
  shutter: () => audio.play('shutter'),
  photographed: names => conn?.send({ type: 'photo', names }),
});
snap.onToggle = on => { input.clear(); audio.play(on ? 'open' : 'close'); document.body.classList.toggle('snapping', on); };

// ---------- settings ----------
function applySettings(st: Settings): void {
  audio.setVolumes({ master: st.master, music: st.music, sfx: st.sfx, ui: st.ui, others: st.others });
  audio.playMusic(st.musicOn && !!pred);
  const root = document.documentElement.style;
  root.setProperty('--ui-scale', String(st.uiScale)); root.setProperty('--pad-scale', String(st.padSize));
  root.setProperty('--chat-size', String(st.chatSize)); root.setProperty('--chat-lines', String(st.chatLines)); root.setProperty('--chat-bg', String(st.chatBg));
  document.documentElement.dataset.theme = st.theme;
  document.querySelector('.pad')?.classList.toggle('lefty', st.leftHanded);
  chat.hidden = new Set(st.chatHide);
  debug.show(st.stats);
  sky.effects = st.weatherFx;
  userZoom = st.zoom;
  maps.vessels = st.vessels;
  resize();
  sendPrefs(st);
}
let sentPrefs = '';
/** The party settings each person keeps: the server needs them (auto-Raise, auto-potion), and the party sees them. */
function sendPrefs(st: Settings, force = false): void {
  const k = `${st.autoRaise}|${st.autoPotion}`; if (!conn || (!force && k === sentPrefs)) return;
  sentPrefs = k; conn.send({ type: 'prefs', autoPotion: Number(st.autoPotion), autoRaise: st.autoRaise });
}
settings.onChange = applySettings;
const layout = new Layout();
settings.onLayout = () => { settings.toggle(false); layout.edit(true); };
// windows that change size (party joins, the quest box fills) stay on screen where they were put
setInterval(() => { if (!layout.editing) layout.apply(); }, 2000);
settings.tabs.push(modsTab(mods, () => settings.toggle(true)));
settings.tabs.push(feedbackTab((rating, kind, text) => conn?.send({ type: 'feedback', rating, kind: kind as 'idea', text })));
debug.onToggle = on => settings.set('stats', on);
// a soft click for every button in the interface
addEventListener('click', e => { if ((e.target as HTMLElement).closest?.('button')) audio.play('click'); }, true);
chat.onLine = (ch, mine) => { if (mine) return; if (ch === 'whisper') audio.play('whisper'); else if (ch === 'party' || ch === 'alliance') audio.play('ping'); };
const nodeReady = new Map<string, number>();   // forage spot -> time it refills for me (ms)
const newReq = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

let conn: Connection | null = null;
let pred: Predictor | null = null;
let me = { id: 0, name: '', faction: 1, look: null as Look | null };
const world = new RemoteWorld(() => me.id);
const bubbles = new Map<number, { text: string; from: number; until: number }>();
let dlg: Dialogue | null = null;
let inputHist: number[] = [];
let layer = 1, zoneName = '';
let acc = 0;

// ---------- canvas ----------
const cv = document.getElementById('world') as HTMLCanvasElement, ctx = cv.getContext('2d')!;
let S = 3, VW = 320, VH = 200;
/** The camera's zoom: eased toward the big-battle fit times the player's own pinch zoom (1 = normal). */
let camZ = 1, userZoom = 1;
// pinch (or ctrl + scroll) zooms the camera; two fingers tapped twice put it back
const ZOOM_MIN = 0.7, ZOOM_MAX = 1.8;
let zoomSave = 0;
input.zoom = () => userZoom;
input.onZoom = z => { userZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, z)); clearTimeout(zoomSave); zoomSave = window.setTimeout(() => settings.set('zoom', Math.round(userZoom * 100) / 100), 400); };
input.onZoomReset = () => { userZoom = 1; settings.set('zoom', 1); };
const people = new People(), portraits = new People();
/** Game pixels at the bottom of the screen hidden under the touch pad; the dialogue box sits above them. */
let padInset = 0;
function resize(): void {
  // the canvas's own size, not innerHeight: on phones the browser bars make 100vh taller than what you can see
  const cssW = cv.clientWidth || innerWidth, cssH = cv.clientHeight || innerHeight;
  const dpr = window.devicePixelRatio || 1, w = Math.round(cssW * dpr), h = Math.round(cssH * dpr);
  S = Math.max(1, Math.min(w, h) / 290);   // ~290 game pixels across the shorter side, drawn at full resolution
  VW = Math.ceil(w / S); VH = Math.ceil(h / S);
  cv.width = w; cv.height = h;
  (ink as any).setRes(Math.min(2.2, Math.max(0.8, S / 3)));   // props and buildings are cached at about screen resolution
  const pad = document.querySelector<HTMLElement>('.pad');
  let top = cssH;
  if (pad && getComputedStyle(pad).display !== 'none') for (const c of pad.querySelectorAll<HTMLElement>('.stick-home, .btns')) top = Math.min(top, c.getBoundingClientRect().top);
  const covered = Math.max(0, cssH - top);
  document.documentElement.style.setProperty('--pad-h', covered + 'px');
  padInset = Math.ceil(covered * dpr / S);
}
addEventListener('resize', resize); visualViewport?.addEventListener('resize', resize); addEventListener('orientationchange', () => setTimeout(resize, 200)); resize();

// ---------- sign in and characters ----------
function drawSprite(canvas: HTMLCanvasElement, look: Look): void { paintLook(canvas, look); }
const gate = new Gate(drawSprite);
const hudBtns = document.getElementById('hudBtns')!;
gate.onPlay = play;
// ---------- the title film, the creator, the intro and waking up ----------
const cinema = new Cinema();
cinema.onZone = id => { if (!pred) setMap(id); };
cinema.onSky = o => { if (!pred) { sky.override = o; scheduleNpcs(npcs, sky.hour(), 0, true); } };
const seen = (k: string) => { try { return sessionStorage.getItem(k) === '1'; } catch { return false; } }, saw = (k: string) => { try { sessionStorage.setItem(k, '1'); } catch { /* private mode */ } };
/** The title card drifting behind the sign-in screens. */
function titleBackdrop(): void { if (cinema.active) return; void cinema.play(TITLE, TITLE.shots.length - 1); cinema.finish(true); }
gate.intro = async () => {
  await cinema.play(TITLE, seen('tt-title') ? TITLE.shots.length - 1 : 0);   // the whole film once a visit; after that, the title card
  saw('tt-title');
  if (settings.s.musicOn) { audio.song('town'); audio.playMusic(true); }
};
const creator = new Creator({
  api, sound: n => audio.play(n),
  back: () => { titleBackdrop(); void gate.showChars(); },
  created: c => {
    // a new character: the intro film (skippable), then they wake up at home
    const summary = { id: c.id, name: c.name, faction: c.faction, look: c.look, lastSeen: null };
    audio.playMusic(false);
    // each homeland has its own film: the continent, its town, then home (Sunvale's is the original intro)
    const shots = homeland(nationOfFaction(c.faction).id).intro.filter(sh => { try { getMap(sh.zone); return true; } catch { return false; } });
    void cinema.play(shots.length ? { shots } : INTRO).then(() => { cinema.stop(); play(summary); });
  },
});
creator.wire();
gate.onCreate = () => { cinema.stop(); audio.playMusic(false); creator.open(); };
void gate.start();
const cinePadWas: boolean[] = [];
/** Any controller button moves the film along (the game's own input isn't running yet). */
function pollCinePad(): void {
  const gp = [...(navigator.getGamepads?.() ?? [])].find(g => g && g.connected); if (!gp) return;
  gp.buttons.forEach((b, i) => { if (b.pressed && !cinePadWas[i]) cinema.pad(); cinePadWas[i] = b.pressed; });
}
/** A brand-new character waking up in bed: lying there a moment, a stretch, then they're yours. */
let wake: { t0: number } | null = null;
const WAKE_MS = 3600;

function play(c: CharacterSummary): void {
  cinema.stop(); creator.close();
  conn = new Connection({ character: c.id, quiet: () => settings.s.quiet });
  me = { id: 0, name: c.name, faction: c.faction, look: c.look };
  conn.onMessage = onMessage;
  conn.onStatus = (s, why) => {
    if (s === 'online') { setStatus(''); gate.hideQueue(); }
    else if (s === 'reconnecting') setStatus(conn?.everOnline ? 'Reconnecting…' : 'Connecting…');
    else if (s === 'stopped') { setStatus(''); leaveWorld(); void gate.showChars(why || 'Disconnected.'); }
  };
  conn.start();
}

// ---------- battles (in an arena of their own) ----------
type Circle = Extract<ServerMsg, { type: 'circles' }>['list'][number];
let circles: Circle[] = [];
/** Fights you were in: you don't watch your own fight's dust cloud (or a copy of you hopping about) when you're back. */
const myFights = new Set<number>();
let playerScreen = { x: 0, y: 0 };   // where you are on the canvas (pixels), for the battle intro
const strokes: Stroke[] = [];
/** Where a fighter is drawn right now (feet), and how tall they are: you, another player, or a creature. */
function fighterAt(id: number): { x: number; y: number; top: number } | null {
  if (id === me.id && pred) { const p = pred.render(0, 0); return { x: p.x, y: p.y, top: 24 }; }
  const r = world.remotes.get(id), ps = r ? world.pose(r) : null; if (!ps || !r) return null;
  return { x: ps.x, y: ps.y, top: r.info?.kind === 'mob' ? mobTop(r.info.mob) : 24 };
}
const battle = new BattleScreen({
  me: () => me.id,
  items: () => {
    const n = new Map<string, number>();
    for (const s of bag.slots.values()) if (itemDef(s.def)?.restore) n.set(s.def, (n.get(s.def) ?? 0) + s.qty);
    return [...n].map(([def, qty]) => ({ def, qty }));
  },
  send: (cmd, target, skill) => conn?.send({ type: 'battle', cmd: cmd as never, target, skill }),
  sound: (name, pitch) => audio.play(name, undefined, pitch),
  song: id => audio.song(id),
  snapshot: () => { const c = document.createElement('canvas'); c.width = cv.width; c.height = cv.height; c.getContext('2d')!.drawImage(cv, 0, 0); return { cv: c, ...playerScreen }; },
  fx: (who, fx) => { const p = fighterAt(who); if (p) strokes.push(...spellAt(fx, p.x, p.y, p.top, performance.now(), 1 / 3)); },
  num: (who, text, color, big) => { const p = fighterAt(who); if (p) floaters.push({ x: p.x + (Math.random() - 0.5) * 6, y: p.y - p.top - 4, text, color, big: !!big, t0: performance.now() }); },
  flash: who => { const now = performance.now(); if (who === me.id) myHurtAt = now; else { const fx = mobFx.get(who) ?? { hurtAt: 0, deadAt: 0 }; fx.hurtAt = now; mobFx.set(who, fx); } },
  tabOrder: () => settings.s.tabOrder,
  shake: (amp, ms) => { const now = performance.now(); if (now + ms > shakeUntil || amp > shakeAmp) { shakeUntil = now + ms; shakeMs = ms; shakeAmp = Math.max(amp, now < shakeUntil ? shakeAmp : 0); } },
  land: who => { const fx = mobFx.get(who) ?? { hurtAt: 0, deadAt: 0 }; fx.airborne = false; fx.landAt = performance.now(); mobFx.set(who, fx); const p = fighterAt(who); if (p) { I.burst('quake', p.x * 3, p.y * 3, 18, 220, 10); I.burst('sand', p.x * 3, p.y * 3, 20, 260, 14); } },
  pos: id => fighterAt(id),
  markTarget: id => conn?.send({ type: 'mark', target: battle.mark === id ? null : id }),
  down: who => { const fx = mobFx.get(who) ?? { hurtAt: 0, deadAt: 0 }; fx.deadAt = performance.now(); mobFx.set(who, fx); const p = fighterAt(who); if (p && world.remotes.get(who)?.info?.kind === 'mob') I.burst('ghost', p.x * 3, (p.y - 6) * 3, 14, 120, 10); },
  waitPref: () => settings.s.waitMode,
  setWaitPref: on => settings.set('waitMode', on),
});
let pendingCommend: Extract<ServerMsg, { type: 'commend_ask' }> | null = null;
battle.onClose = () => { strokes.length = 0; if (pendingCommend) { social.commendAsk(pendingCommend.battle, pendingCommend.mates); pendingCommend = null; } };
// the formation picker: leaders and solo players pick; everyone sees it
const formation = new FormationUI();
let companions = 0;
formation.onPick = id => conn?.send({ type: 'formation', id });
function syncFormation(): void {
  const p = party.party, inParty = !!p && p.members.length > 1;
  formation.set(formation.id, inParty ? p!.members.length : 1 + companions, !inParty || party.isLeader);
}
function setCircles(list: Circle[]): void { circles = list; forgetBrawls(new Set(list.map(c => c.id))); if (pred) pred.circles = list.filter(c => !c.can && !c.mine && !c.done); }

function leaveWorld(): void {
  follow.stop();
  conn?.leave(); conn = null; pred = null; me.id = 0; dlg = null;
  world.reset(); bag.toggle(false); party.reset(); charUI.reset(); mods.stopAll(); snap.toggle(false); dev.setEnabled(false); audio.playMusic(false); hudBtns.hidden = true; nodeReady.clear(); maps.setPlaying(false); battle.close(); setCircles([]); formation.setPlaying(false);
  wake = null; sky.override = null; titleBackdrop();
}
document.getElementById('queueLeave')!.addEventListener('click', () => { conn?.send({ type: 'unqueue' }); leaveWorld(); gate.hideQueue(); void gate.showChars(); });
document.getElementById('chatBtn')!.addEventListener('click', () => input.onChatKey(false));
// phones: the less-used buttons fold away behind ⋯
{ const more = document.getElementById('moreBtn')!;
  more.addEventListener('click', e => { e.stopPropagation(); const open = hudBtns.classList.toggle('open'); more.setAttribute('aria-expanded', String(open)); more.textContent = open ? '×' : '⋯'; });
  hudBtns.addEventListener('click', e => { if (e.target !== more && hudBtns.classList.contains('open')) { hudBtns.classList.remove('open'); more.setAttribute('aria-expanded', 'false'); more.textContent = '⋯'; } }); }
document.getElementById('charsBtn')!.addEventListener('click', () => { leaveWorld(); void gate.showChars(); });
bag.onOp = op => conn?.send({ type: 'inv_op', req: newReq(), ...op } as ClientMsg);
bag.worn = () => { const m = new Map<string, string>(); for (const [slot, it] of charUI.equip) m.set(EQUIP_SLOTS[slot], it.def); return m; };
bag.job = () => myJob;
charUI.onUnequip = slot => conn?.send({ type: 'inv_op', req: newReq(), op: 'unequip', slot });
charUI.onNotes = text => conn?.send({ type: 'notes', text });
charUI.onJob = id => conn?.send({ type: 'job', id });
party.onRaise = char => conn?.send({ type: 'raise', char });
party.canRaise = () => myJob === 'cleric' && myJobLv >= (abilityDef('raise')?.level ?? 10) && !battle.active;
charUI.canChangeJob = () => zoneNow === 'guild' || zoneNow === `home:${party.myChar}`;
// the Mounts tab
const mountsHost: MountsHost = {
  view: null,
  have: def => { let n = 0; for (const sl of bag.slots.values()) if (sl.def === def) n += sl.qty; return n; },
  gold: () => bag.gold,
  atStable: () => zoneNow.split('~')[0] === 'gate',
  atDock: () => !!pred && map.id === 'world' && Math.hypot(DOCK_TILE[0] * TS + 8 - pred.state.x / SUB, DOCK_TILE[1] * TS + 8 - pred.state.y / SUB) <= 40,
  myName: () => me.name,
  pick: id => conn?.send({ type: 'mount', op: 'pick', id }),
  buy: id => conn?.send({ type: 'mount', op: 'buy', id, req: newReq() }),
  fund: gold => conn?.send({ type: 'airship', op: 'fund', gold, req: newReq() }),
  board: () => conn?.send({ type: 'airship', op: 'board' }),
  paint: (cv, kind) => { const g = cv.getContext('2d')!; g.clearRect(0, 0, cv.width, cv.height); const k = cv.height / 26; for (const layer of ['back', 'front'] as const) drawMount(g, kind, cv.width / 2, cv.height - 3, 0, DIR_TH[0] - 0.5, false, false, 0, layer, 0, k); },
};
charUI.mounts = mountsHost;
charUI.drawSprite = (cv, look, dir) => paintLook(cv, look, { dir, job: myJob, key: 'me-' + dir });
party.onOp = op => conn?.send({ type: 'party', ...op });
party.onFriend = op => conn?.send({ type: 'friend', ...op } as ClientMsg);
const social = new SocialUI();
social.send = m => conn?.send({ type: 'social', ...m } as ClientMsg);
social.whisper = name => { if (pred) { input.clear(); chat.openBox(false, `/w ${name} `); } };
social.invite = name => party.onOp({ op: 'invite', name });
social.addFriend = name => party.onFriend({ op: 'add', name });
chat.onName = (name, x, y) => social.open(name, x, y);
chat.isMuted = name => social.isMuted(name);
party.onProfile = (name, x, y) => social.open(name, x, y);
party.onSocial = m => social.send(m);
party.onReport = name => social.report(name);
party.isMuted = name => social.isMuted(name);
party.onMute = (name, on) => social.mute(name, on);
dev.onSim = sim => conn?.send({ type: 'dev', op: 'sim', sim });
dev.onStats = () => conn?.send({ type: 'dev', op: 'stats' });
dev.onGo = zone => conn?.send({ type: 'dev', op: 'go', zone });
dev.onLayout = () => conn?.send({ type: 'layout', op: 'edit' });
dev.onCompanions = count => conn?.send({ type: 'dev', op: 'companions', count });
dev.onJobLevel = level => conn?.send({ type: 'dev', op: 'joblevel', level });
dev.onSky = (hour, weather) => conn?.send({ type: 'dev', op: 'sky', hour, weather: weather as Weather | null });
dev.client = () => ({ fps: Math.round(fps), worstFrameMs: Math.round(worstFrame), onScreen: world.remotes.size, corrections: pred?.corrections ?? 0, snaps: pred?.snaps ?? 0, ping: Math.round(conn?.rtt ?? 0), sounds: audio.playing });
party.onWhisper = name => { if (pred) { input.clear(); chat.openBox(false, `/w ${name} `); } };
chat.onCommand = c => {
  if (c.cmd === 'friend') return party.onFriend({ op: 'add', name: c.name });
  if (c.cmd === 'invite' || c.cmd === 'ainvite') return party.onOp({ op: c.cmd, name: c.name });
  if (c.cmd === 'kick' || c.cmd === 'promote') {
    const char = party.memberByName(c.name);
    return char ? party.onOp({ op: c.cmd, char }) : chat.system(`${c.name} isn't in your party.`);
  }
  if (c.cmd === 'seek') return party.onOp({ op: 'seek', on: c.on, note: c.note });
  if (c.cmd === 'finder') return party.toggleFinder(true);
  if (c.cmd === 'follow') return startFollow(c.name);
  if (c.cmd === 'unfollow') return follow.stop();
  if (c.cmd === 'find') { finding = c.name ? { name: c.name, at: null, said: false } : null; conn?.send({ type: 'locate', name: c.name || null }); if (!c.name) chat.system('Stopped looking.'); return; }
  if (c.cmd === 'mute' || c.cmd === 'unmute') { social.mute(c.name, c.cmd === 'mute'); return chat.system(c.cmd === 'mute' ? `Muted ${c.name} (on this device). /unmute ${c.name} to hear them again.` : `Unmuted ${c.name}.`); }
  if (c.cmd === 'block' || c.cmd === 'unblock') return social.send({ op: c.cmd, name: c.name });
  if (c.cmd === 'report') return social.report(c.name);
  if (c.cmd === 'check') return social.open(c.name, innerWidth / 2 - 110, innerHeight / 3);
  party.onOp({ op: c.cmd });
};

function onMessage(m: ServerMsg): void {
  switch (m.type) {
    case 'welcome':
      sendPrefs(settings.s, true); charUI.homeTown = nationOfFaction(m.faction).town;
      zoneNow = m.zone.id; layer = m.zone.layer; zoneName = m.zone.name; party.myLayer = layer;
      sky.setServerTime(m.now); sky.override = m.sky; sky.zone = m.zone.map; scheduleNpcs(npcs, sky.hour(), 0, true);
      me.id = m.you; me.name = m.name; social.myName = m.name; me.faction = m.faction; me.look = m.look; charUI.name = m.name; charUI.setLook(m.look); chat.myName = m.name; party.myChar = m.character; dev.setEnabled(!!m.dev);
      setMap(m.zone.map); maps.setPlaying(true); formation.setPlaying(true); maps.setZone(m.zone.id, m.zone.name, m.zone.layer); showHp();
      if (pred) pred.resetTo(m.state, m.seq); else pred = new Predictor(m.state, m.seq, map);
      inputHist = []; acc = 0; world.reset(); gate.hide(); hudBtns.hidden = false; resize();   // the touch pad shows again
      chat.system(m.resumed ? 'Reconnected.' : `Welcome to ${m.zone.name}${m.zone.layer > 1 ? ` (layer ${m.zone.layer})` : ''}, ${m.name}.`);
      myBind = m.bind ?? 'town';
      if (!m.resumed) audio.song('town');   // (a fight song left over from before is gone)
      wake = m.fresh && !m.resumed ? { t0: performance.now() } : null;
      audio.playMusic(settings.s.musicOn);
      conn?.send({ type: 'battle', cmd: 'wait', skill: settings.s.waitMode ? 'on' : 'off' });
      mods.start();
      mods.emit('ready', { name: m.name, faction: m.faction, zone: m.zone.name, layer: m.zone.layer });
      break;
    case 'info': world.addInfo(m.ents); for (const i of m.ents) if (i.id === me.id && i.look) { me.look = i.look; charUI.setLook(i.look); } break;
    case 'snap': world.applySnapshot(m); pred?.reconcile(m.ack, m.you); break;
    case 'chat':
      chat.fromServer(m); mods.emit('chat', { ch: m.ch, name: m.name, text: m.text, to: m.to ?? null });
      if (m.ch === 'say' && !(m.name !== me.name && social.isMuted(m.name))) bubbles.set(m.from, { text: m.text, from: performance.now(), until: performance.now() + 4000 });
      break;
    case 'card': social.card(m.card); break;
    case 'blocks': party.setBlocked(m.list); break;
    case 'commend_ask': if (battle.active) pendingCommend = m; else social.commendAsk(m.battle, m.mates); break;   // after the battle screen closes
    case 'error': chat.system(m.msg); audio.play('error'); break;
    case 'inv': bag.setAll(m.slots, m.gold); charUI.setEquip(m.equip); emitInventory(); break;
    case 'inv_update': bag.apply(m.set, m.clear, m.gold); emitInventory(); charUI.refreshMounts(); if (m.eq) { charUI.applyEquip(m.eq); audio.play(m.eq.set.length ? 'open' : 'close'); } break;
    case 'quests': charUI.setQuests(m.list); mods.emit('quests', [...charUI.quests.values()]); break;
    case 'quest_update': charUI.updateQuest(m.quest); mods.emit('quests', [...charUI.quests.values()]); break;
    case 'journal': charUI.setJournal(m.entries, m.notes); break;
    case 'journal_add': charUI.addJournal(m.entry); break;
    case 'req_fail': toast(m.msg, 'err'); audio.play('error'); break;
    case 'skills': bag.setXp(m.xp); charUI.setXp(m.xp); emitSkills(); showXp(); break;
    case 'skill_xp': bag.addXp(m.skill, m.xp); charUI.addXp(m.skill, m.xp); emitSkills(); showXp();
      if (m.skill === jobSkill(myJob) && (m.gained ?? 0) > 0 && pred) { const x = pred.state.x / SUB, y = pred.state.y / SUB; floaters.push({ x, y: y - 34, text: `+${m.gained} EXP`, color: '#c99a2e', big: false, t0: performance.now() }); document.getElementById('xpBar')!.classList.remove('gain'); void document.getElementById('xpBar')!.offsetWidth; document.getElementById('xpBar')!.classList.add('gain'); }
      break;
    case 'notice': mods.emit('notice', { kind: m.kind, text: m.text }); toast(m.text, m.kind); if (m.kind === 'loot') audio.play('loot'); else if (m.kind === 'level') audio.play('level'); if (m.kind !== 'loot') chat.system(m.text); break;
    case 'nodes': { const now = performance.now(); for (const [id, secs] of m.cooling) nodeReady.set(id, now + secs * 1000); break; }
    case 'pong': sky.setServerTime(m.now, conn?.rtt ?? 0); break;
    case 'sky': sky.override = m.sky; chat.system(m.sky ? 'A developer changed the sky.' : 'The sky is back to normal.'); break;
    case 'queue': gate.showQueue(m.position, m.size, m.etaSec); setStatus(''); break;
    case 'party_state': { const had = !!party.party; party.setState(m); syncFormation(); mods.emit('party', { party: m.party, alliance: m.alliance, seeking: m.seeking }); if (!had && m.party && m.party.members.length > 1) audio.play('join'); break; }
    case 'party_offer': party.offer(m); audio.play('invite'); break;
    case 'party_offer_gone': party.offerGone(m.offer); break;
    case 'finder': party.setFinder(m); break;
    case 'dev_state': dev.set(m); break;
    case 'friends': party.setFriends(m.list); break;
    case 'friend_update': { const was = party.friends.get(m.friend.char); party.friendUpdate(m.friend); if (was && was.online !== m.friend.online) chat.system(`${m.friend.name} is ${m.friend.online ? 'online' : 'offline'}.`); break; }
    case 'friend_gone': party.friendGone(m.char); break;
    case 'friend_offer': party.friendOffer(m); audio.play('invite'); break;
    case 'fallen': fallenUntil = m.until; showFallen(); break;
    case 'raise_offer': if (settings.s.autoRaise) { conn?.send({ type: 'fallen', op: 'accept' }); break; }
      ask(`${m.from} offers a Raise`, 'Get back up where you are, with a third of your health?', [{ label: 'Accept', primary: true, run: () => conn?.send({ type: 'fallen', op: 'accept' }) }, { label: 'Decline', run: () => conn?.send({ type: 'fallen', op: 'decline' }) }], '✨'); audio.play('open'); break;
    case 'weak': weakUntil = m.until; showWeak(); break;
    case 'allegiance': me.faction = nationById(m.nation).faction; charUI.homeTown = nationById(m.nation).town; charUI.setAllegiance(m.nation, m.pts); if (m.gained) chat.system(`+${m.gained} allegiance to ${nationById(m.nation).name}.`); break;
    case 'located': {
      if (!finding) break;
      if (!m.found) { chat.system(m.name ? `Couldn't find ${m.name} anywhere in Tiny Town right now.` : 'They left.'); finding = null; break; }
      finding.at = m.here && m.x != null ? { x: m.x, y: m.y! } : null;
      if (!finding.said || finding.zone !== `${m.zone}~${m.layer}`) { chat.system(m.here ? `${m.name} is here: look for the pink ring on your minimap.` : `${m.name} is in ${m.zone}${m.layer && m.layer > 1 ? ` (layer ${m.layer})` : ''}.`); finding.said = true; finding.zone = `${m.zone}~${m.layer}`; finding.name = m.name; }
      break;
    }
    case 'presence': {   // "Still there?": pick the shape it names
      const ICON: Record<string, string> = { star: '⭐', heart: '❤️', moon: '🌙', leaf: '🍃', flower: '🌸', bell: '🔔' };
      ask('Still there?', `You've been fighting for hours! Pick the ${m.ask} to carry on (rewards pause if nobody answers in ${Math.round(m.secs / 60)} minutes).`, m.shapes.map((sh, i) => ({ label: `${ICON[sh] ?? sh} ${sh}`, run: () => conn?.send({ type: 'presence', id: m.id, pick: i }) })), '👋');
      audio.play('open'); break;
    }
    case 'loot_roll': lootRolls.add(m); audio.play('open'); break;
    case 'loot_done': chat.system(lootRolls.done(m)); break;
    case 'hidden': shimmerAt = m.at; break;
    case 'grotto': grottoCleared = m.cleared; break;
    case 'layouts': for (const l of m.list) setLayout(l.room, l.furniture); break;
    case 'garden': gardenView = m.garden; gardenSkew = m.garden.now - Date.now(); break;
    case 'layout_edit': if (m.room) roomEdit.open(m.room); else roomEdit.close(); break;
    case 'mark': battle.setMark(m.target); if (m.target != null) chat.system(`${m.by} marked a target.`); break;
    case 'hp': myHp = m.hp; myMax = m.max; if (m.maxMp != null) { myMp = m.mp ?? 0; myMaxMp = m.maxMp; } showHp(); break;
    case 'job': myJob = m.id; myJobLv = m.level; if (m.jobs) charUI.jobs = new Set(m.jobs); charUI.setJob(m.id); showHp(); break;
    case 'mounts': mountsHost.view = m.view; charUI.refreshMounts(); break;
    case 'boss': maps.boss = { open: m.open, at: m.at }; break;
    case 'alert': alerts.set(m.id, performance.now()); audio.play('invite', undefined, 1.3); break;
    case 'bound': myBind = m.zone; shrineFlare = performance.now(); audio.play('level'); audio.play('holy', undefined, 1.2); chat.system(`You're bound to the wayshrine in ${m.name}. If you faint, you'll wake up here.`); break;
    case 'food': myFood = m.id ? { id: m.id, until: m.until } : null; showFood(); break;
    case 'battle': { if (!battle.active) { dlg = null; follow.stop(); } if (m.start) for (const f of m.battle.fighters) if (f.side === 'foe' && f.mob && mobDef(f.mob)?.boss && m.battle.arena) mobFx.set(f.id, { ...(mobFx.get(f.id) ?? { hurtAt: 0, deadAt: 0 }), airborne: true }); battle.update(m); tutor.update(m.battle, m.events, me.id, myJob, fighterAt(me.id)); showHorde(m.battle.horde); const mine = m.battle.fighters.find(f => f.id === me.id); if (mine) { myHp = mine.hp; myMax = mine.max; if (mine.maxMp != null) { myMp = mine.mp ?? 0; myMaxMp = mine.maxMp; } } break; }
    case 'battle_end': myFights.add(m.id); battle.end(m); tutor.end(); showHorde(undefined); break;
    case 'circles': setCircles(m.list); break;
    case 'formation': formation.id = m.id; companions = m.companions; syncFormation(); break;
    case 'hits': onHits(m.list); break;
    case 'zone': if (m.zone.map !== 'grotto') grottoCleared = false; shimmerAt = null; zoneNow = m.zone.id; gardenView = null; setTimeout(() => charUI.refreshMounts(), 0); follow.newZone(); charUI.setJob(myJob); setCircles([]); doorWait = performance.now() + 400; doorPush = 0; setMap(m.zone.map); maps.setZone(m.zone.id, m.zone.name, m.zone.layer); showHp(); if (!m.zone.map.startsWith('arena-')) sky.zone = m.zone.map; /* a fight keeps the weather of where it started */ if (pred) pred.resetTo(m.state, pred.seq); world.reset(); audio.play('open'); layer = m.zone.layer; party.myLayer = layer; if (!m.zone.map.startsWith('arena-') && !zoneName.startsWith('Battle')) chat.system(m.zone.name === zoneName ? `Now on layer ${m.zone.layer}.` : `Entered ${m.zone.name}.`); zoneName = m.zone.name; break;
  }
}

/** Hits and heals the server saw this tick. */
function onHits(list: [number, number, number, number, number][]): void {
  const now = performance.now();
  for (const [target, amt, hp, src, flags] of list) {
    if (target === me.id) {
      const p = pred ? { x: pred.state.x / SUB, y: pred.state.y / SUB } : { x: 0, y: 0 };
      if (flags & HIT.heal) { if (amt >= 10 && hp < myMax) floaters.push({ x: p.x, y: p.y - 30, text: `+${amt}`, color: '#4f9a43', big: false, t0: now }); }
      else if (flags & HIT.dodge) { floaters.push({ x: p.x, y: p.y - 30, text: 'dodge!', color: '#6f88c2', big: false, t0: now }); audio.play('dodge'); }
      else { floaters.push({ x: p.x, y: p.y - 30, text: String(amt), color: '#c9443a', big: false, t0: now }); I.burst('slash', p.x * 3, (p.y - 12) * 3, 6, 140, 5); myHurtAt = now; audio.play('hurt'); hpBar.classList.remove('hurt'); void hpBar.offsetWidth; hpBar.classList.add('hurt'); }
      myHp = hp; showHp();
      continue;
    }
    const r = world.remotes.get(target), ps = r ? world.pose(r) : null;
    if (r?.info?.kind === 'mob') r.info = { ...r.info, hp };
    const fx = mobFx.get(target) ?? { hurtAt: 0, deadAt: 0 }; mobFx.set(target, fx);
    if (flags & HIT.heal) { fx.deadAt = 0; continue; }
    if (flags & HIT.dodge) continue;
    fx.hurtAt = now;
    if (ps) floaters.push({ x: ps.x + (Math.random() - 0.5) * 6, y: ps.y - mobTop(r?.info?.mob) - 4, text: (flags & HIT.crit) ? `${amt}!` : String(amt), color: src === me.id ? ((flags & HIT.crit) ? '#f28b4b' : '#e05a4c') : '#9a8a7e', big: !!(flags & HIT.crit), t0: now });
    if (ps) I.burst((flags & HIT.crit) ? 'light' : 'slash', ps.x * 3, (ps.y - mobTop(r?.info?.mob) * 0.5) * 3, (flags & HIT.crit) ? 12 : 7, 160, 6);
    if (ps && (flags & HIT.kill)) { I.burst('ghost', ps.x * 3, (ps.y - 6) * 3, 14, 120, 10); I.spawn({ type: 'ring', x: ps.x * 3, y: ps.y * 3, life: 0.45, r: 30, col: '#fbf6ea' }); }
    if (flags & HIT.kill) { fx.deadAt = now; audio.play('poof', ps && pred ? { dx: ps.x - pred.state.x / SUB, dy: ps.y - pred.state.y / SUB } : undefined, src === me.id ? 1 : 0.9); }
    else if (src === me.id) audio.play('hit', undefined, (flags & HIT.crit) ? 1.25 : 1);
  }
}

// ---------- the maps ----------
maps.scene = id => { const m = getMap(id); return sceneFor(id, m.data.collision, m.w, m.h); };
maps.people = () => {
  const party_ = new Map<string, string[]>(), friends = new Map<string, string[]>();
  const add = (to: Map<string, string[]>, zone: string, name: string) => { const z = zoneOfName(zone); if (z) (to.get(z) ?? (to.set(z, []), to.get(z)!)).push(name); };
  for (const m of party.party?.members ?? []) if (m.online && m.char !== party.myChar) add(party_, m.zone, m.name);
  for (const f of party.friends.values()) if (f.online) add(friends, f.zone, f.name);
  return { party: party_, friends };
};
maps.onOpen = open => { input.clear(); audio.play(open ? 'open' : 'close'); };
let miniT = 0;
const roomMinis = new Map<Room, { w: number; h: number; thumb: () => HTMLCanvasElement }>();
const roomMini = (r: Room) => roomMinis.get(r) ?? (roomMinis.set(r, { w: r.def.w, h: r.def.h, thumb: (() => { let t: HTMLCanvasElement | null = null; return () => t ??= roomThumb(r); })() }), roomMinis.get(r)!);
function drawMinimap(now: number): void {
  if (now - miniT < 120 || !pred) return; miniT = now;
  const dots: Dot[] = [], marks: Mark[] = [], mates = party.memberNames();
  for (const r of world.remotes.values()) {
    const ps = world.pose(r); if (!ps || !r.info) continue;
    if (r.info.kind === 'mob') { if ((r.info.hp ?? 1) > 0) dots.push({ x: ps.x, y: ps.y, kind: mobDef(r.info.mob)?.aggro ? 'boss' : 'mob' }); }
    else dots.push({ x: ps.x, y: ps.y, kind: mates.has(r.info.name.toLowerCase()) ? 'party' : 'player' });
  }
  for (const n of npcs) if (n.alpha > 0.5) dots.push({ x: n.x, y: n.y, kind: 'npc' });
  dots.push({ x: pred.state.x / SUB, y: pred.state.y / SUB, kind: 'me', dir: pred.state.dir });
  for (const d of DOORS) if (d.map === map.id) { const [x, y] = doorPoint(d, TS); marks.push({ x, y, kind: d.id.startsWith('line-') ? 'exit' : 'door', label: d.label }); }
  const ws = zoneDef(map.id.split('~')[0])?.wayshrine; if (ws && scene) marks.push({ x: ws[0] * TS + 8, y: ws[1] * TS + 8, kind: 'shrine', label: 'Wayshrine' });
  for (const c of circles) marks.push({ x: c.x, y: c.y, kind: 'battle', label: 'Battle', open: c.can, r: c.r });
  if (finding?.at) marks.push({ x: finding.at.x, y: finding.at.y, kind: 'find', label: finding.name });
  maps.me = [pred.state.x / SUB, pred.state.y / SUB];
  maps.draw(scene ?? (room ? roomMini(room) : null), pred.state.x / SUB, pred.state.y / SUB, dots, marks, now);
}

// ---------- what mods can see ----------
function emitInventory(): void {
  const named = (s: { def: string; qty: number; slot: number }) => ({ slot: s.slot, def: s.def, name: itemDef(s.def)?.name ?? s.def, qty: s.qty });
  mods.emit('inventory', { gold: bag.gold, bag: [...bag.slots.values()].map(named), equip: [...charUI.equip.values()].map(named) });
}
function emitSkills(): void { mods.emit('skills', Object.fromEntries(Object.entries(charUI.xp).map(([k, xp]) => [k, { xp, level: levelForXp(xp) }]))); }
let modPosT = 0, modPos = '';
function emitPosition(now: number): void {
  if (!pred || now - modPosT < 250) return; modPosT = now;
  const x = pred.state.x / SUB / TS, y = pred.state.y / SUB / TS, key = x.toFixed(1) + y.toFixed(1) + layer;
  if (key !== modPos) { modPos = key; mods.emit('position', { x, y, zone: zoneName, layer }); }
  if (Math.floor(now / 1000) !== Math.floor((now - 250) / 1000)) mods.emit('players', { inView: world.remotes.size });
}

// ---------- input wiring ----------
input.enabled = () => !dlg && !choiceOpen() && !chat.open && !snap.active && !wake && document.getElementById('worldmap')!.hidden;
input.onChatKey = slash => { if (pred && !dlg) { input.clear(); chat.openBox(slash); } };
input.onBagKey = () => { if (pred) { bag.toggle(); audio.play(bag.open ? 'open' : 'close'); } };
input.onPartyKey = () => { if (pred) party.toggleFinder(); };
input.onPad = btn => battle.pad(btn);
input.dpadMoves = () => !battle.active;
input.onMenuKey = () => { if (pred) charUI.toggle(); };
input.onMapKey = () => { if (pred) maps.toggleWorld(); };

/** The door you're standing at, if any. */
function nearbyDoor(px: number, py: number): typeof DOORS[number] | null {
  let best: typeof DOORS[number] | null = null, bd = 1e9;
  for (const d of DOORS) { if (d.map !== map.id) continue; const [x, y] = doorPoint(d, TS), dist = Math.hypot(x - px, y - py); if (dist <= DOOR_RANGE_PX && dist < bd) { bd = dist; best = d; } }
  return best;
}
/** Start a conversation (a quest giver's scene, if they have one for you now). */
const actionOf = (n: Npc): string | undefined => (n as Npc & { action?: string }).action;
/** Turn the page in a conversation; at the end, anyone with something to offer asks (a mentor, the trainer...). */
function talkNext(now: number): void {
  if (!dlg) return;
  const n = dlg.npc, bye = !!dlg.bye; dlg = advance(dlg, now);
  if (!dlg && !bye && actionOf(n)) offer(n);
}
/** What a townsperson offers when you've finished talking: a job, a lesson, a challenge. */
function offer(n: Npc): void {
  const [kind, arg] = (actionOf(n) ?? '').split(':');
  if (kind === 'mentor') {
    const m = mentorFor(arg, map.id); if (!m) return;
    const j = jobDef(m.job), had = charUI.jobs.has(m.job), w = itemDef(m.weapon)?.name ?? 'a weapon';
    if (myJob === m.job) return;
    if (battle.active) return;
    const advLv = levelForXp(charUI.xp[jobSkill('adventurer')] ?? 0);
    if (!had && !charUI.jobs.size && advLv < JOB_UNLOCK_LEVEL) { ask(`${m.name}`, `"Come back when you've some road behind you." A mentor teaches you a job once your Adventurer reaches level ${JOB_UNLOCK_LEVEL} (you're level ${advLv}). Until then: Kick, First Aid, Stone Throw and Brace come to you on the road.`, [{ label: 'All right', primary: true }], j.icon); return; }
    ask(had ? `Be a ${j.name} again?` : `Become a ${j.name}?`, had ? `${m.name} will gladly have you back. Changing to a job you've taken up is free.` : `${j.role}. ${m.name} will teach you (you start at level ${JOB_UNLOCK_LEVEL}), and give you ${/^[aeiou]/i.test(w) ? 'an' : 'a'} ${w} to start with.`,
      [{ label: had ? `Be a ${j.name}` : `Become a ${j.name}`, primary: true, run: () => conn?.send({ type: 'mentor', job: m.job }) }, { label: 'Not now' }], j.icon);
  } else if (kind === 'consul') {
    const to = nationById(arg), from = nationOfFaction(me.faction);
    if (to.id === from.id) return;
    const pts = charUI.allegiance?.pts ?? 0;
    ask(`Become one of the people of ${to.name}?`, `${to.townName} becomes your home and your allegiance starts afresh: you give up the ${pts} allegiance you earned with ${from.name}. You can move homeland once a week.`,
      [{ label: `Move to ${to.name}`, primary: true, run: () => conn?.send({ type: 'homeland', nation: to.id }) }, { label: 'Stay' }], to.emblem);
  } else if (kind === 'tutorial') {
    ask('Battle Basics?', 'A lesson with a training dummy that fights back (gently), in a ring of your own. Your party comes too. Steps show on screen as you go.',
      [{ label: 'Start the lesson', primary: true, run: () => conn?.send({ type: 'tutorial' }) }, { label: 'Not now' }], '🎯');
  } else if (kind === 'shop' && arg === 'garden') { openGardenShop(n.name);
  } else if (kind === 'horde') {
    const h = hordeDef(arg); if (!h) return;
    ask(`${h.name}?`, `${h.blurb} Your party comes in with you.`, [{ label: 'Bring it on', primary: true, run: () => conn?.send({ type: 'horde', id: h.id }) }, { label: 'Not yet' }], '🌪️');
  }
}
// ---------- your backyard (shared/garden.ts) ----------
let gardenView: GardenView | null = null, gardenSkew = 0;
const bagCount = (def: string) => [...bag.slots.values()].filter(x => x.def === def).reduce((a, x) => a + x.qty, 0);
/** E in the garden: plant (picking the seed if you have several kinds), water, pick; feed or collect from an animal. */
function gardenAct(px: number, py: number): boolean {
  const v = gardenView!, t = gardenTarget(v, px, py); if (!t) return false;
  const send = (m: GardenOp) => conn?.send({ type: 'garden', ...m } as ClientMsg);
  if (!v.mine) { toast(`${v.ownerName || 'Your friend'}'s garden: look, but leave the picking to them.`, 'info'); return true; }
  const now = Date.now() + gardenSkew;
  if ('plot' in t) {
    const p = v.plots[t.plot], g = growth(p, now);
    if (g.stage === 'empty') {
      const seeds = CROPS.filter(c => bagCount(c.seed) > 0);
      if (!seeds.length) { toast('You have no seeds. The seed and feed vendor in town sells them.', 'info'); return true; }
      if (seeds.length === 1) { send({ op: 'plant', plot: t.plot, seed: seeds[0].seed }); return true; }
      ask('Plant what?', 'Pick a seed for this plot.', [...seeds.map(c => ({ label: `${c.name} (${bagCount(c.seed)})`, run: () => send({ op: 'plant', plot: t.plot, seed: c.seed }) })), { label: 'Not now' }], '🌱');
      return true;
    }
    if (g.stage === 'ripe') { send({ op: 'harvest', plot: t.plot }); return true; }
    const bit = g.stage === 'sprout' ? 1 : 2;
    if (bagCount('watering_can') && !(p.watered & bit)) { send({ op: 'water', plot: t.plot }); return true; }
    toast(`${cropDef(p.crop)?.name ?? 'It'}: ready in about ${Math.max(1, Math.ceil(g.leftMs / 60_000))} minutes${p.watered & bit ? ' (watered)' : ''}.`, 'info');
    return true;
  }
  const a = t.animal, def = ANIMALS[a.kind];
  if (animalReady(a, now)) send({ op: 'collect', animal: a.id });
  else if (now - a.fedAt > FED_MS) { if (bagCount('animal_feed')) send({ op: 'feed', animal: a.id }); else toast(`${a.name} is hungry, and you have no feed. The seed and feed vendor sells it.`, 'info'); }
  else toast(`${a.name} the ${def.name.toLowerCase()} is happy. Next ${itemDef(def.produce)?.name.toLowerCase() ?? def.produce} in about ${Math.max(1, Math.ceil((def.everyMs - (now - a.lastCollect)) / 60_000))} minutes.`, 'info');
  return true;
}
/** The seed and feed vendor's wares: tap one to buy (seeds and feed in fives). */
function openGardenShop(vendor: string): void {
  const qty = (item: string) => item.endsWith('_seed') || item === 'animal_feed' ? 5 : 1;
  ask(`${vendor}'s seeds and feed`, `You have ${bag.gold.toLocaleString()} gil. Seeds and feed come in fives; animals go straight to your backyard.`,
    [...GARDEN_SHOP.map(x => ({ label: `${x.label ?? itemDef(x.item)?.name ?? x.item}${qty(x.item) > 1 ? ` ×${qty(x.item)}` : ''} · ${x.price * qty(x.item)} gil`,
      run: () => { conn?.send({ type: 'buy', item: x.item, qty: qty(x.item) }); setTimeout(() => openGardenShop(vendor), 400); } })), { label: 'Done' }], '🧺');
}
function beginTalk(n: Npc, x: number, y: number, now: number): void {
  dlg = startTalk(n, x, y, now);
  const q = questTalk(n.name); if (q) { dlg.lines = q.lines; if (q.send) conn?.send({ type: 'talk', npc: n.name }); }
}
/** Talking to a quest giver: their scene, if talking to them is your next step (and the server is told), or a
 *  reminder of what they asked. */
function questTalk(name: string): { lines: [string, string, string][]; send: boolean } | null {
  for (const v of charUI.quests.values()) {
    if (v.state !== 'active') continue;
    const d = questDef(v.id); if (!d || d.giver !== name) continue;
    const g = d.goals[d.goals.findIndex((x, k) => (v.progress[k] ?? 0) < goalNeed(x))];
    if (g?.kind === 'talk' && g.npc === name) return { lines: d.scene?.length ? d.scene : [['n', 'smile', d.story]], send: true };
    return { lines: [['n', 'smile', d.story], ['n', 'neutral', g ? `Next: ${g.text}.` : 'Nearly there!']], send: false };
  }
  return null;
}
let lockedHintT = 0, overworld = false, dockHintT = 0, wasShip = false;
let doorPush = 0, doorWait = 0, wasVz = 0, wasMount = 0, wasFlying = false, rideHintT = 0;
function useDoor(d: typeof DOORS[number]): void { if (performance.now() < doorWait) return; doorWait = performance.now() + 700; conn?.send({ type: 'interact', req: newReq(), target: d.id }); }

/** Binding: the crystal flares, a pillar of light goes up and rings of light ripple out over the ground. */
function drawShrineFlare(ms: number): void {
  const ws = zoneDef(map.id.split('~')[0])?.wayshrine; if (!ws) return;
  const x = ws[0] * TS + 8, y = ws[1] * TS + 12, k = ms / 1800, fade = k < 0.15 ? k / 0.15 : 1 - (k - 0.15) / 0.85;
  ctx.save(); ctx.globalCompositeOperation = 'lighter';
  for (let i = 0; i < 3; i++) { const r = ((k * 1.6 + i / 3) % 1) * 46; ctx.strokeStyle = `rgba(170,220,255,${0.7 * fade * (1 - r / 46)})`; ctx.lineWidth = 1.6; ctx.beginPath(); ctx.ellipse(x, y, r, r * 0.42, 0, 0, Math.PI * 2); ctx.stroke(); }
  const gr = ctx.createLinearGradient(0, y - 120, 0, y); gr.addColorStop(0, 'rgba(190,230,255,0)'); gr.addColorStop(0.7, `rgba(190,230,255,${0.45 * fade})`); gr.addColorStop(1, `rgba(255,255,255,${0.7 * fade})`);
  const w = 10 + Math.sin(k * Math.PI) * 6; ctx.fillStyle = gr; ctx.fillRect(x - w / 2, y - 120, w, 120);
  const glow = ctx.createRadialGradient(x, y - 26, 0, x, y - 26, 30); glow.addColorStop(0, `rgba(220,245,255,${0.9 * fade})`); glow.addColorStop(1, 'rgba(220,245,255,0)');
  ctx.fillStyle = glow; ctx.fillRect(x - 30, y - 56, 60, 60);
  for (let i = 0; i < 10; i++) { const a = i * 2.4, rise = ((k * 1.4 + i / 10) % 1); ctx.fillStyle = `rgba(255,255,255,${fade * (1 - rise)})`; ctx.fillRect(x + Math.cos(a) * 10 * (1 - rise), y - 8 - rise * 70, 1.4, 1.4); }
  ctx.restore();
}
/** The wayshrine you're standing at, if any (tile centre, pixels). */
function nearbyShrine(px: number, py: number): { x: number; y: number } | null {
  if (!scene) return null;
  const ws = zoneDef(map.id.split('~')[0])?.wayshrine; if (!ws) return null;
  const x = ws[0] * TS + 8, y = ws[1] * TS + 8;
  return Math.hypot(x - px, y - py) <= INTERACT_RANGE_PX + 10 ? { x, y } : null;
}
/** The forage spot you'd pick from right now, if any. */
function nearbyNode(px: number, py: number): typeof FORAGE[number] | null {
  const now = performance.now(); let best: typeof FORAGE[number] | null = null, bd = 1e9;
  for (const n of FORAGE) {
    if (n.map !== map.id || (nodeReady.get(n.id) ?? 0) > now || !nodeOpen(n)) continue;
    const d = Math.hypot(n.tile[0] * TS + 8 - px, n.tile[1] * TS + 8 - py);
    if (d <= INTERACT_RANGE_PX && d < bd) { bd = d; best = n; }
  }
  return best;
}
document.getElementById('findForm')!.addEventListener('submit', e => { e.preventDefault(); const n = (document.getElementById('findName') as HTMLInputElement).value.trim(); if (n) { finding = { name: n, at: null, said: false }; conn?.send({ type: 'locate', name: n }); } });
document.getElementById('findStop')!.addEventListener('click', () => { finding = null; conn?.send({ type: 'locate', name: null }); });
document.getElementById('fallenHome')!.addEventListener('click', () => conn?.send({ type: 'fallen', op: 'home' }));
// a question card takes the keys while it's up (before the game sees them)
addEventListener('keydown', e => { if (choiceOpen() && choiceKey(e.key)) { e.preventDefault(); e.stopImmediatePropagation(); } }, true);
chat.onSend = (ch, text, to) => conn?.send({ type: 'chat', ch, text, to });
const myPos = () => pred ? { x: pred.state.x / SUB, y: pred.state.y / SUB } : { x: 0, y: 0 };
input.onAct = () => {
  if (!pred) return;
  const now = performance.now();
  if (choiceOpen()) return;
  if (dlg) { talkNext(now); return; }
  const p = myPos(), n = nearbyNpc(npcs, p.x, p.y, DIR_NAMES[pred.state.dir]);
  if (n) { beginTalk(n, p.x, p.y, now); input.clear(); return; }
  if (map.id === 'garden' && gardenView && gardenAct(p.x, p.y)) return;   // your backyard: the plot or the pen you're next to
  const door = nearbyDoor(p.x, p.y);
  if (door && (door.id.startsWith('ferry-') || door.id.startsWith('sky-'))) {   // the pier: every crossing from here, by sea or by air
    const all = DOORS.filter(d => d.map === door.map && d.tile[0] === door.tile[0] && d.tile[1] === door.tile[1]);
    ask('Where to?', 'Ferries are cheaper and need a ferry pass (Neighbour at home) to go abroad; airships are faster and need an airship pass (Resident). Both cross a lot of ocean, and things sometimes climb aboard.',
      [...all.map(d => ({ label: (d.id.startsWith('sky-') ? '🎈 ' : '⛴ ') + d.label, primary: d === all[0], run: () => useDoor(d) })), { label: 'Not now' }], '⚓');
    return;
  }
  if (door) { useDoor(door); return; }
  if (map.id === 'grotto' && Math.hypot(p.x - (GROTTO_CHEST[0] * TS + 8), p.y - (GROTTO_CHEST[1] * TS + 8)) < 28) { conn?.send({ type: 'interact', req: newReq(), target: 'grotto-chest' }); return; }
  const node = nearbyNode(p.x, p.y);
  if (node) { conn?.send({ type: 'interact', req: newReq(), target: node.id }); nodeReady.set(node.id, now + 1500); return; }   // dim it until the server answers
  if (nearbyShrine(p.x, p.y) && !battle.active) conn?.send({ type: 'interact', req: newReq(), target: 'wayshrine' });
};
input.onAttackPress = () => { if (choiceOpen()) return true; if (dlg) { talkNext(performance.now()); return true; } if (battle.active) { battle.attack(); return true; } return false; };

// ---------- the loop ----------
function simTick(): void {
  if (!pred || !conn) return;
  const before = pred.state.act;
  const own = input.sample(), st0 = pred.state;
  // auto-follow presses the buttons for you (not while fighting or talking); your own keys take back over
  let bits = follow.active && !battle.active && !dlg && !st0.seat ? follow.steer(own, { x: st0.x, y: st0.y, mount: !!st0.mount, flying: !!st0.flying }, followTarget(), map, performance.now()) : own;
  // the Sky Dock: R boards your airship (your party nearby comes too)
  const dockD = map.id === 'world' ? Math.hypot(DOCK_TILE[0] * TS + 8 - st0.x / SUB, DOCK_TILE[1] * TS + 8 - st0.y / SUB) : 1e9;
  if ((bits & IN.mount) && dockD < 36 && !st0.mount && !st0.seat && mountsHost.view?.airship.owned) { conn.send({ type: 'airship', op: 'board' }); bits &= ~IN.mount; }
  if (dockD < 20 && !st0.mount && performance.now() - dockHintT > 8000) { dockHintT = performance.now(); toast(mountsHost.view?.airship.owned ? 'Sky Dock: press R to board your airship.' : 'Sky Dock: build an airship here (your party can chip in). Opening your Mounts tab…', 'info'); if (!mountsHost.view?.airship.owned) charUI.toggle(true, 'mounts'); }
  if (st0.mount === AIRSHIP && !wasShip) toast('All aboard! Fly anywhere, even over the mountains. Press R over open ground to land.', 'info');
  wasShip = st0.mount === AIRSHIP;
  pred.frozen = battle.active ? battle.fighter(me.id)?.state === 'down' : fallenUntil > Date.now();   // fallen: you can't move until you're Raised
  const seq = pred.tick(bits);
  // walking into a doorway goes through it: up into buildings, down out of rooms, right down Cottage Lane
  const door = pred.state.seat || pred.state.mount === AIRSHIP ? null : nearbyDoor(pred.state.x / SUB, pred.state.y / SUB);   // riding with someone, or on an airship: no doors
  if (door?.step) { const [dx, dy] = doorPoint(door, TS); if (Math.hypot(dx - pred.state.x / SUB, dy - pred.state.y / SUB) < 12) useDoor(door); }
  if (overworld && performance.now() - lockedHintT > 5000) for (const pl of PLACES) if (pl.locked && (pl.world ?? 'world') === map.id.split('~')[0] && Math.hypot(pl.tile[0] * TS + 8 - pred.state.x / SUB, pl.tile[1] * TS + 8 - pred.state.y / SUB) < 20) { lockedHintT = performance.now(); toast(`${pl.name}: ${pl.locked}`, 'info'); }
  const into = door && !door.step ? IN[pushOf(door)] : 0;
  doorPush = door && (bits & into) ? doorPush + 1 : 0;
  if (door && doorPush >= 6) useDoor(door);   // keeps trying while you push (useDoor waits between tries)
  if (pred.state.act !== before) { if (pred.state.act === Act.Dash) audio.play('dash'); else if (pred.state.act === Act.Attack) { audio.play('attack'); swingNear(pred.state.x / SUB, pred.state.y / SUB, pred.state.dir, true); } }
  const st = pred.state;
  if (st.vz > 0 && wasVz <= 0 && !st.flying) audio.play('jump', undefined, st.mount ? 0.85 : 1);
  if (st.mount !== wasMount) audio.play(st.mount ? 'mount' : 'close');
  if (st.flying && !wasFlying) audio.play('dodge');
  if (!st.mount && (bits & IN.mount) && !map.mounts && performance.now() - rideHintT > 3000) { rideHintT = performance.now(); toast('Mounts are for the open world. Head out past the Town Gate!', 'info'); }
  wasVz = st.vz; wasMount = st.mount; wasFlying = st.flying; input.flying = st.flying;
  inputHist.push(bits); if (inputHist.length > INPUT_REDUNDANCY) inputHist.shift();
  if (seq % INPUT_SEND_EVERY === 0) conn.send({ type: 'input', seq, inputs: inputHist.slice(), ack: world.lastSnapId });
}

let talking = false, skyT = 0;
let last = performance.now(), fps = 0, fpsN = 0, fpsT = last, dbgT = 0, sweepT = 0, worstFrame = 0, worstNow = 0;
function frame(now: number): void {
  const gap = now - last; worstNow = Math.max(worstNow, gap);
  const dt = Math.min(0.1, gap / 1000); last = now;
  world.advance(dt);
  if (now - sweepT > 5000) { sweepT = now; people.sweep(now); for (const k of [...remoteAnim.keys()]) if (!world.remotes.has(k)) remoteAnim.delete(k); for (const [k, b] of bubbles) if (b.until < now) bubbles.delete(k); }
  if (pred && conn?.status === 'online') {
    acc += dt * 1000; let n = 0;
    while (acc >= TICK_MS && n < 8) { simTick(); acc -= TICK_MS; n++; }
    if (n === 8) acc = 0;
  }
  LIFE.rain = sky.amount('rain') + sky.amount('storm'); LIFE.outdoors = !room;
  scheduleNpcs(npcs, sky.hour(), dt, false, map);
  politeGoodbye(dlg, now);   // their shift ended while you were chatting: they say goodbye, then go
  if (snap.active) { if (!pred) snap.toggle(false); else snap.frame(dt); }
  else if (!creator.active) { draw(acc / TICK_MS, dt, now); if (battle.active) battle.frame(now); }
  if (cinema.active && !cinema.backdrop) pollCinePad();
  emitPosition(now);
  drawMinimap(now);
  if (now - skyT > 250) {
    skyT = now;
    audio.setAmbience(pred && sky.effects ? Math.min(1, sky.amount('rain') + sky.amount('storm')) * (room ? 0.35 : 1) : 0, pred ? sky.night() && !room : false);
    const label = sky.label(); if (clockEl.textContent !== label) clockEl.textContent = label;
    clockEl.hidden = !pred;
  }
  if (talking !== !!dlg) { talking = !!dlg; document.body.classList.toggle('talking', talking); }   // chat steps aside for the dialogue box
  fpsN++; if (now - fpsT > 1000) { fps = fpsN * 1000 / (now - fpsT); fpsN = 0; fpsT = now; worstFrame = worstNow; worstNow = 0; }
  if (now - dbgT > 250) { dbgT = now; updateDebug(); }
  requestAnimationFrame(frame);
}
applySettings(settings.s);
requestAnimationFrame(frame);

// ---------- drawing ----------
const I = ink as any;
const remoteAnim = new Map<number, number>();   // last animation seen per player, to play their dash and attack sounds

/** The nearest `n` people, but someone already chosen stays chosen until they drop well past the cut
 *  (`slack` places further). Without this, two people at nearly the same distance trade places every frame
 *  and their name tags and speech bubbles blink on and off. */
const nameKeep = new Set<number>(), calmKeep = new Set<number>(), detailKeep = new Set<number>();
function stableNearest(items: { id: number; d: number }[], n: number, slack: number, prev: Set<number>): Set<number> {
  items.sort((a, b) => a.d - b.d);
  const out = new Set<number>();
  items.forEach((it, i) => { if (i < n || (i < n + slack && prev.has(it.id))) out.add(it.id); });
  prev.clear(); for (const id of out) prev.add(id);
  return out;
}

/** Someone riding behind (or aboard with) the person being drawn. */
interface Rider { id: number | string; look?: KitLook | null; job?: string }
/** Role rings this frame: the names (lowercase) of party members to ring, or null when they're off. */
let ringNames: Set<string> | null = null;
/** A soft coloured ring on the ground by role (protector blue, healer green, damage red). */
function roleRing(x: number, y: number, job: string | undefined, now: number): void {
  const role = job ? ROLE_OF[job as JobId] : null; if (!role) return;
  const c = ROLE_COLOR[role], p = 0.75 + Math.sin(now / 500) * 0.1;
  ctx.save(); ctx.globalAlpha *= p; ctx.fillStyle = c + '33'; ctx.strokeStyle = c; ctx.lineWidth = 1.4;
  ctx.beginPath(); ctx.ellipse(x, y + 1, 11, 4.5, 0, 0, Math.PI * 2); ctx.fill(); ctx.stroke(); ctx.restore();
}
interface Folk { id: number | string; name?: string; look?: KitLook | null; job?: string; x: number; y: number; z: number; m: number; kind: number; dir: number; moving: boolean; attacking: boolean; riders: Rider[]; face?: string | null; cheap?: boolean; fallen?: boolean }
/** Someone standing, jumping, riding or flying: shadow, the mount, then the person on top (and anyone riding with
 *  them). On the world map everyone is drawn at half size. Returns the top of their head, for names and bubbles. */
function drawFolk(o: Folk, dt: number, now: number): number {
  const k = overworld ? 0.5 : 1, lift = o.z * k, ship = o.kind === AIRSHIP;
  if (ringNames && o.name && !overworld && !ship && ringNames.has(o.name.toLowerCase())) roleRing(o.x, o.y, o.job, now);
  const c = people.step(o.id, o.look, { job: o.job }, o.x, o.y, o.dir, o.attacking, dt, now, o.face, !!o.m || ship);
  const rider = (r: Rider) => people.step(r.id, r.look, { job: r.job }, o.x, o.y, o.dir, false, dt, now, null, true);
  if (ship) {   // the captain and crew on deck, under the balloon
    const face = o.dir === 3 ? -1 : 1, ks = overworld ? 0.45 : 1;
    const { deckY } = drawAirship(ctx, o.x, o.y, SHIP_LIFT, o.dir, now, 'back', ks);
    o.riders.forEach((r, i) => drawInkPerson(ctx, rider(r), o.x - face * (8 + (i % 4) * 9) * ks, deckY - Math.floor(i / 4) * 3 * ks, 0, ks, now, true));
    drawInkPerson(ctx, c, o.x + face * 12 * ks, deckY, 0, ks, now, true);
    drawAirship(ctx, o.x, o.y, SHIP_LIFT, o.dir, now, 'front', ks);
    return o.y - SHIP_LIFT * ks - 168 * ks / 3;   // names go over the balloon
  }
  if (o.fallen) {   // lying where they fell: on their side, a little ghostly, with a slow halo of stars
    ctx.save(); ctx.translate(o.x, o.y - 3); ctx.rotate(-Math.PI / 2); ctx.translate(-o.x, -o.y);
    ctx.globalAlpha *= 0.85; drawInkPerson(ctx, c, o.x, o.y, 0, k, now, true); ctx.restore();
    for (let i = 0; i < 3; i++) { const a = now / 700 + i * 2.1; ctx.fillStyle = '#f2cf4a'; ctx.fillRect(o.x - 14 + Math.cos(a) * 6, o.y - 10 + Math.sin(a) * 2.5, 1.6, 1.6); }
    return o.y - 14;
  }
  const seat = o.m ? SEAT * k : 0, th = c.th, Fx = Math.cos(th), Fy = Math.sin(th), seed = typeof o.id === 'number' ? o.id : 7;
  if (o.m) drawMount(ctx, o.kind || 1, o.x, o.y, lift, th, o.m === 2, o.moving, now, 'back', seed, k);
  const back = o.riders[0] ? rider(o.riders[0]) : null;
  const drawBack = () => { if (back) drawInkPerson(ctx, back, o.x - Fx * 7 * k, o.y - Fy * 3 * k, lift + seat, k, now, true); };
  if (Fy >= 0) drawBack();
  if (o.cheap) drawCheap(ctx, c, o.x, o.y, lift + seat, k); else drawInkPerson(ctx, c, o.x, o.y, lift + seat, k, now, !!o.m);
  if (Fy < 0) drawBack();
  if (o.m) drawMount(ctx, o.kind || 1, o.x, o.y, lift, th, o.m === 2, o.moving, now, 'front', seed, k);
  return o.y - lift - seat - personTop(c, k);
}

/** The arena floor: the glowing gate out, big moves' shapes (fill up as they wind up: step out!), and a ring
 *  under the creature you're aiming at. */
function drawArenaFloor(now: number): void {
  const b = battle.b!;
  if (b.exit) {
    const [x, y] = b.exit, p = 0.6 + Math.sin(now / 300) * 0.15, mine = battle.fighter(me.id)?.escape ?? 0;
    const gr = ctx.createRadialGradient(x, y, 2, x, y, 18); gr.addColorStop(0, `rgba(255,236,170,${0.55 * p})`); gr.addColorStop(1, 'rgba(255,236,170,0)');
    ctx.fillStyle = gr; ctx.beginPath(); ctx.ellipse(x, y, 18, 13, 0, 0, Math.PI * 2); ctx.fill();
    ctx.setLineDash([3, 2.5]); ctx.lineDashOffset = -now / 80; ctx.lineWidth = 1; ctx.strokeStyle = `rgba(212,154,50,${p + 0.2})`; ctx.beginPath(); ctx.ellipse(x, y, 13, 9, 0, 0, Math.PI * 2); ctx.stroke(); ctx.setLineDash([]);
    if (mine > 0) { ctx.lineWidth = 2; ctx.strokeStyle = '#f2c14e'; ctx.beginPath(); ctx.ellipse(x, y, 15, 11, 0, -Math.PI / 2, -Math.PI / 2 + mine * Math.PI * 2); ctx.stroke(); }
  }
  if (b.horde?.ring) {   // the ring closing in: everything outside it is the storm
    const [rx, ry, rr] = b.horde.ring;
    ctx.save(); ctx.beginPath(); ctx.rect(-2000, -2000, 6000, 6000); ctx.arc(rx, ry, rr, 0, Math.PI * 2, true); ctx.fillStyle = 'rgba(92,60,120,0.28)'; ctx.fill('evenodd');
    ctx.setLineDash([6, 4]); ctx.lineDashOffset = now / 50; ctx.lineWidth = 1.6; ctx.strokeStyle = 'rgba(178,120,230,0.9)'; ctx.beginPath(); ctx.arc(rx, ry, rr, 0, Math.PI * 2); ctx.stroke(); ctx.setLineDash([]); ctx.restore();
  }
  for (const q of b.tele) {
    const k = 1 - q.left / Math.max(1, q.total), pulse = 0.5 + Math.sin(now / 120) * 0.2;
    if (q.hazard === 'rocks' && k > 0.55) { const fall = (k - 0.55) / 0.45; ctx.fillStyle = '#8a7a6a'; ctx.beginPath(); ctx.ellipse(q.x, q.y - 120 * (1 - fall), 6, 5, 0, 0, Math.PI * 2); ctx.fill(); ctx.lineWidth = 1; ctx.strokeStyle = '#3e2f28'; ctx.stroke(); }   // the rock coming down
    if (q.hazard === 'quake') for (let i = 0; i < 5; i++) { const a2 = i * 1.26 + q.id; ctx.strokeStyle = `rgba(120,60,30,${0.3 + k * 0.5})`; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(q.x, q.y); ctx.lineTo(q.x + Math.cos(a2) * q.r * k, q.y + Math.sin(a2) * q.r * k * 0.6); ctx.stroke(); }   // cracks spreading
    ctx.fillStyle = 'rgba(224,90,76,0.13)'; ctx.beginPath(); ctx.arc(q.x, q.y, q.r, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = `rgba(224,90,76,${0.18 + k * 0.2})`; ctx.beginPath(); ctx.arc(q.x, q.y, q.r * k, 0, Math.PI * 2); ctx.fill();
    ctx.lineWidth = 1.1; ctx.setLineDash([4, 3]); ctx.lineDashOffset = now / 60; ctx.strokeStyle = `rgba(201,68,58,${0.6 + pulse * 0.4})`; ctx.beginPath(); ctx.arc(q.x, q.y, q.r, 0, Math.PI * 2); ctx.stroke(); ctx.setLineDash([]);
    ctx.lineWidth = 0.5; ctx.strokeStyle = 'rgba(62,47,40,0.6)'; ctx.beginPath(); ctx.arc(q.x, q.y, q.r + 1, 0, Math.PI * 2); ctx.stroke();
  }
  const t = battle.aim != null ? fighterAt(battle.aim) : null;
  if (t) {
    ctx.setLineDash([3, 2]); ctx.lineDashOffset = -now / 70; ctx.lineWidth = 1.1; ctx.strokeStyle = '#f2c14e';
    const r = Math.max(9, t.top * 0.45); ctx.beginPath(); ctx.ellipse(t.x, t.y + 1, r, r * 0.36, 0, 0, Math.PI * 2); ctx.stroke(); ctx.setLineDash([]);
  }
}
/** Over the arena: spell strokes, and a little time-bar ring at each friend's feet (gold when they can act). */
function drawArenaOver(now: number): void {
  { const mk = battle.mark != null ? fighterAt(battle.mark) : null;   // the leader's mark: a bobbing star over it
    if (mk) { const y = mk.y - mk.top - 10 + Math.sin(now / 250) * 1.5; I.star4(ctx, mk.x, y, 5, now / 900, '#f2cf4a', 1.2); } }
  for (let i = strokes.length - 1; i >= 0; i--) { const s = strokes[i]; if (now > s.t0 + s.dur) { strokes.splice(i, 1); continue; } drawStroke(ctx, s, now); }
  const b = battle.b; if (!b) return;
  for (const f of b.fighters) {
    if (f.side !== 'party' || f.state === 'down') continue;
    const p = fighterAt(f.id); if (!p) continue;
    const full = b.ready.includes(f.id), k = f.cast ? 1 - f.cast.left / Math.max(1, f.cast.total) : Math.min(1, f.atb / 1000), mine = f.id === me.id;
    ctx.lineWidth = mine ? 1.4 : 0.9; ctx.strokeStyle = 'rgba(62,47,40,0.35)'; ctx.beginPath(); ctx.ellipse(p.x, p.y + 1, 8, 3, 0, 0, Math.PI * 2); ctx.stroke();
    ctx.strokeStyle = f.cast ? '#9a7cc2' : full ? '#f2c14e' : '#6fb2cc'; ctx.beginPath(); ctx.ellipse(p.x, p.y + 1, 8, 3, 0, -Math.PI / 2, -Math.PI / 2 + k * Math.PI * 2); ctx.stroke();
    if (full && mine && Math.floor(now / 400) % 2 === 0) { ctx.fillStyle = 'rgba(242,193,78,0.25)'; ctx.beginPath(); ctx.ellipse(p.x, p.y + 1, 9, 3.4, 0, 0, Math.PI * 2); ctx.fill(); }
  }
}

function drawProp(pr: Scene['props'][number], t: number): void {
  if (pr.live) return pr.live(ctx, t);
  const sp = pr.spr; if (!sp) return;
  const hit = pr.kind === 'dummy' ? dummyHits.get(pr) : undefined, k = hit ? (performance.now() - hit) / 1000 : 9;
  if (k < 1.2) {   // a training dummy rocks on its post after a hit, and settles
    const a = Math.sin(k * 18) * 0.28 * Math.exp(-k * 3.2) * (dummyDir.get(pr) ?? 1), sq = 1 - Math.max(0, 0.12 - k) * 1.2;
    ctx.save(); ctx.translate(pr.x, pr.y); ctx.rotate(a); ctx.scale(1 / sq, sq); ctx.translate(-pr.x, -pr.y);
    ctx.drawImage(sp.c, pr.x - sp.ox / 3, pr.y - sp.oy / 3, sp.w / 3, sp.h / 3); ctx.restore(); return;
  }
  ctx.drawImage(sp.c, pr.x - sp.ox / 3, pr.y - sp.oy / 3, sp.w / 3, sp.h / 3);
}
const dummyHits = new Map<Scene['props'][number], number>(), dummyDir = new Map<Scene['props'][number], number>();
const FACE4: [number, number][] = [[0, 1], [1, 0], [0, -1], [-1, 0]];
/** Someone swung a weapon at (x, y) facing `dir`: any training dummy in front of them takes the hit. */
function swingNear(x: number, y: number, dir: number, mine: boolean): void {
  if (!scene) return;
  const [fx, fy] = FACE4[dir] ?? FACE4[0], cx = x + fx * 12, cy = y + fy * 10;
  for (const pr of scene.props) {
    if (pr.kind !== 'dummy' || Math.hypot(pr.x - cx, pr.y - 4 - cy) > 20) continue;
    setTimeout(() => {
      dummyHits.set(pr, performance.now()); dummyDir.set(pr, fx >= 0 ? 1 : -1);
      I.burst('slash', pr.x * 3, (pr.y - 18) * 3, 8, 200, 6); I.burst('sand', pr.x * 3, (pr.y - 18) * 3, 5, 90, 5);   // sparks and a puff of straw
      if (mine) { const crit = Math.random() < 0.15, amt = Math.round((crit ? 2 : 1) * (4 + Math.random() * 6) * (1 + myJobLv * 0.15)); floaters.push({ x: pr.x + (Math.random() - 0.5) * 6, y: pr.y - 30, text: crit ? `${amt}!` : String(amt), color: crit ? '#f28b4b' : '#e05a4c', big: crit, t0: performance.now() }); }
      audio.play('hit', mine ? undefined : { dx: pr.x - myPos().x, dy: pr.y - myPos().y }, 0.85 + Math.random() * 0.2);
    }, 260);
  }
}

let lastCam = { cx: 0, cy: 0, sc: 1 };
// the room editor: click a piece to pick it, drag to move it
const roomEdit = new RoomEditor();
/** Rearranged furniture: the room's walls and picture change (and, if you're in it, at once). */
function setLayout(roomId: string, list: Furniture[]): void {
  applyLayout(roomId, list); rooms.delete(roomId);
  if (map.id === roomId || (roomId === 'home' && map.id === 'home')) { const r = interiorDef(roomId); if (r) { room = buildRoom(r); rooms.set(roomId, room); } map = getMap(map.id); pred?.setMap(map); }
}
roomEdit.onPreview = (id, list) => setLayout(id, list);
roomEdit.onSave = list => conn?.send({ type: 'layout', op: 'save', furniture: list });
roomEdit.onCancel = () => conn?.send({ type: 'layout', op: 'cancel' });
{ let grab: [number, number] | null = null;
  const tileAt = (e: PointerEvent): [number, number] => { const r = cv.getBoundingClientRect(), dpr = cv.width / r.width; return [Math.floor(((e.clientX - r.left) * dpr / lastCam.sc + lastCam.cx) / TS), Math.floor(((e.clientY - r.top) * dpr / lastCam.sc + lastCam.cy) / TS)]; };
  cv.addEventListener('pointerdown', e => { if (!roomEdit.active || !room) return; const [tx, ty] = tileAt(e); roomEdit.pick(tx, ty); const f = roomEdit.list[roomEdit.sel]; grab = f ? [tx - f.x, ty - f.y] : null; });
  cv.addEventListener('pointermove', e => { if (grab && roomEdit.active) { const [tx, ty] = tileAt(e); roomEdit.drag(tx, ty, grab); } });
  addEventListener('pointerup', () => { grab = null; });
}
// in a battle, a click or tap on a creature aims at it
cv.addEventListener('pointerdown', e => {
  if (!battle.active) return;
  const r = cv.getBoundingClientRect(), dpr = cv.width / r.width, x = (e.clientX - r.left) * dpr / lastCam.sc + lastCam.cx, y = (e.clientY - r.top) * dpr / lastCam.sc + lastCam.cy;
  let best: number | null = null, bd = 22;
  for (const f of battle.b?.fighters ?? []) { if (f.side !== 'foe' || f.hp <= 0) continue; const p = fighterAt(f.id); if (!p) continue; const d = Math.hypot(p.x - x, p.y - p.top / 2 - y); if (d < bd) { bd = d; best = f.id; } }
  if (best != null) battle.setAim(best);
});
function draw(alpha: number, dt: number, now: number): void {
  ringNames = settings.s.roleRings !== 'off' && (battle.active || settings.s.roleRings === 'always') ? party.memberNames() : null;
  // rooms are small: zoom in so they fill the screen
  const film = !pred ? cinema.camera(now, TS) : null;   // the title or the intro: the film's camera, not you
  if (film && !room) film.zoom = Math.max(film.zoom, cv.width / (MW * TS * S), cv.height / (MH * TS * S));   // never wider than the place (tall phone screens)
  // a big battle (an alliance against a world boss) is fought on a bigger field: ease the camera out to take it in
  const bigFit = map.id.startsWith('arena-') && map.id.endsWith('-big') ? Math.max(S * 0.55, Math.min(S, cv.width / (MW * TS - 40), cv.height / (MH * TS - 20))) / S : 1;
  camZ += (bigFit * (room || film ? 1 : userZoom) - camZ) * Math.min(1, dt * 4);
  const sc = (room ? Math.max(S, Math.min(cv.width / (MW * TS + 40), cv.height / (MH * TS + 70))) : S * camZ) * (film?.zoom ?? 1), vw = Math.ceil(cv.width / sc), vh = Math.ceil(cv.height / sc);
  I.setTime(now / 1000); I.stepFx(dt); sprites.frameStart();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  let p = pred ? pred.render(alpha, dt) : film ? { x: film.x, y: film.y, z: 0 } : { x: map.data.spawn[0] * TS + 8, y: map.data.spawn[1] * TS + 12, z: 0 };
  // riding with someone: you're wherever they are; on an airship, drawn a little above its shadow
  const myRide = pred?.state.seat ? world.remotes.get(pred.state.seat) : undefined, ridePose = myRide ? world.pose(myRide) : null;
  if (ridePose) p = { x: ridePose.x, y: ridePose.y, z: ridePose.k === AIRSHIP ? SHIP_LIFT : ridePose.z };
  else if (pred?.state.mount === AIRSHIP) p = { ...p, z: SHIP_LIFT };
  let camX = Math.max(0, Math.min(MW * TS - vw, p.x - vw / 2)), camY = Math.max(-Math.round(p.z * 0.8), Math.min(MH * TS - vh, p.y - 12 - p.z * 0.6 - vh / 2));   // high up, the camera rises with you
  if (MW * TS < vw) camX = (MW * TS - vw) / 2; if (MH * TS < vh) camY = (MH * TS - vh) / 2;
  if (now < shakeUntil) { const k = (shakeUntil - now) / shakeMs, a = shakeAmp * k; camX += Math.sin(now / 23) * a; camY += Math.cos(now / 31) * a * 0.7; }   // a heavy hit: the screen shakes
  const cx = Math.round(camX * sc) / sc, cy = Math.round(camY * sc) / sc;   // whole screen pixels: no shimmer
  // and you on whole screen pixels too: the camera snaps to the pixel grid, so drawing you at your exact spot
  // would wobble you a fraction of a pixel against it every frame (most visible walking slowly on the world map)
  p = { ...p, x: cx + Math.round((p.x - cx) * sc) / sc, y: cy + Math.round((p.y - cy) * sc) / sc };
  lastCam = { cx, cy, sc };
  const onScreenC = (x: number, y: number) => x > cx - 60 && x < cx + vw + 60 && y > cy - 60 && y < cy + vh + 60;
  playerScreen = { x: (p.x - cx) * sc, y: (p.y - 16 - cy) * sc };
  overworld = zoneDef(map.id.split('~')[0])?.kind === 'world';
  ctx.fillStyle = scene ? scene.edge : '#2f2a26'; ctx.fillRect(0, 0, cv.width, cv.height);   // flying near the edge: more meadow, not a void
  ctx.setTransform(sc, 0, 0, sc, -cx * sc, -cy * sc);
  ctx.imageSmoothingEnabled = true;
  const t = now / 1000;
  if (room) { ctx.drawImage(room.bg, 0, 0, MW * TS, MH * TS); drawWindows(ctx, room, sky.hour(), sky.amount('rain') + sky.amount('storm'), t); }
  if (room && roomEdit.active) {   // the editor: every piece outlined, the picked one in gold
    roomEdit.list.forEach((f, i) => { ctx.setLineDash(i === roomEdit.sel ? [] : [2, 2]); ctx.lineWidth = i === roomEdit.sel ? 1.5 : 0.8; ctx.strokeStyle = i === roomEdit.sel ? '#f2c14e' : f.block ? 'rgba(201,68,58,0.8)' : 'rgba(78,131,173,0.8)'; ctx.strokeRect(f.x * TS + 0.5, f.y * TS + 0.5, f.w * TS - 1, f.h * TS - 1); });
    ctx.setLineDash([]);
  }
  else if (scene) {
    const gk = scene.gk, gx = Math.max(0, Math.floor(cx)), gy = Math.max(0, Math.floor(cy)), gw = Math.min(MW * TS - gx, vw + 2), gh = Math.min(MH * TS - gy, vh + 2);
    if (gw > 0 && gh > 0) ctx.drawImage(scene.ground, gx * gk, gy * gk, gw * gk, gh * gk, gx, gy, gw, gh);
  }
  const propVisible = (pr: Scene['props'][number]) => {
    const sp = pr.spr, x0 = sp ? pr.x - sp.ox / 3 : pr.x - 30, y0 = sp ? pr.y - sp.oy / 3 : pr.y - 50, w = sp ? sp.w / 3 : 60, h = sp ? sp.h / 3 : 60;
    return x0 < cx + vw && x0 + w > cx && y0 < cy + vh && y0 + h > cy;
  };
  if (scene) for (const pr of scene.props) if (pr.flat && propVisible(pr)) drawProp(pr, t);   // lily pads, boats: under everyone
  // battle circles: a ring on the ground (red: a fight, keep out; green: they'd love some help)
  for (const c of circles) if (!c.done && onScreenC(c.x, c.y)) battleRing(ctx, c.x, c.y, c.r, c.can ? 'help' : c.mine ? 'mine' : 'busy', now);
  if (battle.active && battle.b?.arena && map.id.startsWith('arena-')) drawArenaFloor(now);
  if (gardenView && map.id === 'garden') drawGardenGround(ctx, gardenView, Date.now(), gardenSkew);
  for (const n of FORAGE) {
    if (n.map !== map.id || (nodeReady.get(n.id) ?? 0) > now || !nodeOpen(n)) continue;
    const x = n.tile[0] * TS + 2, y = n.tile[1] * TS + 1 + Math.sin(now / 500 + n.tile[0]) * 0.8;
    shadow(ctx, x + 6, n.tile[1] * TS + 14, 9);
    ctx.drawImage(itemIcon(n.item), x, y, 12, 12);
    const tw = (now / 900 + n.tile[1] * 0.37) % 1;
    if (tw < 0.25) I.star4(ctx, x + 11, y + 1, 1.8 * Math.sin(tw / 0.25 * Math.PI), 0, '#fff6d8', 0.4);
  }
  ctx.imageSmoothingEnabled = true;

  // everything that stands on the ground, sorted by feet
  type Item = { y: number; draw: () => void };
  const items: Item[] = [];
  const onScreen = (x: number, y: number) => x > cx - 40 && x < cx + vw + 40 && y > cy - 10 && y < cy + vh + 60;
  if (scene) for (const pr of scene.props) if (!pr.flat && propVisible(pr)) items.push({ y: pr.y, draw: () => drawProp(pr, t) });
  // fights going on in their arenas: the leader and the creature scrapping in a dust cloud where it started
  for (const c of circles) if (c.lead && !c.mine && !myFights.has(c.id) && onScreen(c.x, c.y)) items.push({ y: c.y, draw: () => drawBrawl(ctx, c, people, now, dt, overworld ? 0.6 : 1) });
  // the ferry and the public airship, going about their timetable (the same on every screen)
  if (overworld && settings.s.vessels) for (const v of vesselsAt(Date.now())) {
    const vx = v.x * TS + 8, vy = v.y * TS + 8;
    if (!onScreen(vx, vy - (v.kind === 'airship' ? 40 : 0))) continue;
    items.push({ y: v.kind === 'airship' ? vy + 60 : vy, draw: () => { const top = drawVessel(ctx, v, vx, vy, now); nameTag(ctx, vx, top - 2, v.docked ? `${v.name} · at ${v.docked}` : `${v.name} → ${v.heading}`, v.kind === 'ship' ? '#4e83ad' : '#e3604b', false); } });
  }
  const npcTop = new Map<Npc, number>();
  if (critters.length) items.push(...critterItems(critters.filter(c => onScreen(c.x, c.y)), ctx, now, sky.darkness() > 0.35));
  npcs.forEach((n, i) => {
    if (n.alpha <= 0.02 || !onScreen(n.x, n.y)) return;
    items.push({ y: n.y, draw: () => {
      ctx.globalAlpha = n.alpha;
      const face = dlg?.npc === n && dlg.lines[dlg.i]?.[0] === 'n' ? dlg.lines[dlg.i][1] : null;
      const pose = npcPose(n, dlg?.npc === n);
      const c = people.step(`n${i}`, n.look, { name: n.name, npc: true, tool: pose.tool, work: pose.work }, n.x, n.y, Math.max(0, ['down', 'right', 'up', 'left'].indexOf(n.dir)), false, dt, now, face);
      drawInkPerson(ctx, c, n.x, n.y, n.hop, 1, now);
      if (pose.work) workFx(ctx, n, c, now);
      npcTop.set(n, n.y - personTop(c));
      ctx.globalAlpha = 1;
    } });
  });
  const remotePoses: { r: Remote; x: number; y: number }[] = [];   // y: the top of their head
  const partyNames = overworld ? party.memberNames() : new Set<string>(); let grouped = 0;
  // calmer crowds: people you chose not to focus on are drawn faded
  const othersMode = settings.s.crowd, faded = new Set<number>();
  const playersInView = [...world.remotes.values()].filter(r => r.info?.kind !== 'mob').map(r => ({ r, ps: world.pose(r) })).filter(q => q.ps);
  if (othersMode !== 'all') {
    const close = othersMode === 'friends' ? new Set([...party.memberNames(), ...party.friendNames()]) : null;
    if (close) { for (const q of playersInView) if (!close.has(q.r.info?.name.toLowerCase() ?? '')) faded.add(q.r.id); }
    else {
      const keep = stableNearest(playersInView.map(q => ({ id: q.r.id, d: (q.ps!.x - p.x) ** 2 + (q.ps!.y - p.y) ** 2 })), 30, 8, calmKeep);
      for (const q of playersInView) if (!keep.has(q.r.id)) faded.add(q.r.id);
    }
  }
  // in a big crowd, only the nearest are drawn in full; the rest are simple shapes
  const detailed = playersInView.length > 40 ? stableNearest(playersInView.filter(q => !faded.has(q.r.id)).map(q => ({ id: q.r.id, d: (q.ps!.x - p.x) ** 2 + (q.ps!.y - p.y) ** 2 })), 36, 6, detailKeep) : null;
  const mobPoses: { r: Remote; x: number; y: number }[] = [];
  const riders = new Map<number, Rider[]>(), ridersOf = (id: number) => riders.get(id) ?? (riders.set(id, []), riders.get(id)!);
  for (const r of world.remotes.values()) {
    const ps = world.pose(r); if (!ps || !onScreen(ps.x, ps.y)) continue;
    if (r.info?.kind === 'mob') {
      const fx = mobFx.get(r.id), kind = r.info.mob ?? 'puff', bf = battle.active ? battle.fighter(r.id) : undefined, mhp = bf?.hp ?? r.info.hp ?? 1;
      if (mhp <= 0 && !fx?.deadAt) continue;      // already gone when we arrived
      mobPoses.push({ r, x: ps.x, y: ps.y });
      const was = remoteAnim.get(r.id);
      if (was !== ps.anim) { remoteAnim.set(r.id, ps.anim); if (was !== undefined && ps.anim === Anim.Attack) audio.play('attack', { dx: ps.x - p.x, dy: ps.y - p.y }, 1.35); }
      const alive = mhp > 0;
      // a boss dropping into the arena: up out of sight until it lands, then a quick fall
      const drop = fx?.airborne ? 160 : fx?.landAt && now - fx.landAt < 360 ? 160 * (1 - (now - fx.landAt) / 360) ** 2 : 0;
      if (drop) { const sh = 1 - drop / 200; ctx.fillStyle = `rgba(62,47,40,${0.25 * sh})`; ctx.beginPath(); ctx.ellipse(ps.x, ps.y + 1, 16 * sh + 4, 5 * sh + 1, 0, 0, Math.PI * 2); ctx.fill(); }
      items.push({ y: ps.y, draw: () => drawMob(ctx, { kind, x: ps.x, y: ps.y - drop, dir: ps.dir, moving: alive && (ps.anim === Anim.Walk || ps.anim === Anim.Run), attackK: alive && ps.anim === Anim.Attack ? Math.sin(Math.min(1, ps.t / ATTACK_TICKS) * Math.PI) : 0,
        hp: mhp, hurtAt: fx?.hurtAt ?? 0, deadAt: fx?.deadAt ?? 0, id: r.id }, now) });
      continue;
    }
    if (ps.s) {   // riding with someone: drawn with them
      ridersOf(ps.s).push({ id: r.id, look: r.info?.look, job: r.info?.job });
      if (ps.s !== me.id) continue;
    }
    if (ps.k === AIRSHIP) ps.z = SHIP_LIFT;
    if (overworld && pred && (partyNames.has(r.info?.name.toLowerCase() ?? '') || r.info?.name.endsWith(' (sim)')) && Math.hypot(ps.x - p.x, ps.y - p.y) < 40) { grouped++; continue; }   // your party travels as one little group
    if (ps.s) continue;
    const was = remoteAnim.get(r.id);
    if (was !== ps.anim) {
      remoteAnim.set(r.id, ps.anim);
      if (was !== undefined && (ps.anim === Anim.Dash || ps.anim === Anim.Attack)) audio.play(ps.anim === Anim.Dash ? 'dash' : 'attack', { dx: ps.x - p.x, dy: ps.y - p.y }, 0.92 + (r.id % 7) * 0.025);
      if (was !== undefined && ps.anim === Anim.Attack) swingNear(ps.x, ps.y, ps.dir, false);
    }
    const entry = { r, x: ps.x, y: ps.y - 30 };
    remotePoses.push(entry);
    items.push({ y: ps.y + ps.z, draw: () => {
      const fade = faded.has(r.id);
      if (fade) ctx.globalAlpha = 0.28;
      entry.y = drawFolk({ id: r.id, name: r.info?.name, look: r.info?.look, job: r.info?.job, x: ps.x, y: ps.y, z: ps.z, m: ps.m, kind: ps.k || (ps.m ? 1 : 0), dir: ps.dir, moving: ps.anim === Anim.Walk || ps.anim === Anim.Run,
        attacking: ps.anim === Anim.Attack, riders: riders.get(r.id) ?? [], fallen: battle.active ? battle.fighter(r.id)?.state === 'down' : !!r.info?.fallen, cheap: fade || (detailed ? !detailed.has(r.id) : false) }, dt, now);
      if (fade) ctx.globalAlpha = 1;
    } });
  }
  let myTop = p.y - p.z - 30;
  if (pred && me.look && pred.state.seat) {   // riding with someone: you're drawn with them
    ridersOf(pred.state.seat).push({ id: me.id, look: me.look, job: myJob });
  } else if (pred && me.look && wake && room) {
    // waking up: tucked in on the pillow with the quilt up to the chin, then a stretch and a hop out of bed
    const k = (now - wake.t0) / WAKE_MS, bx = 32, by = 62, up = Math.max(0, (k - 0.55) / 0.3);
    if (k >= 1) { wake = null; input.clear(); chat.system('Morning! Head out the door when you\'re ready. Your neighbours are expecting you.'); }
    const look = me.look;
    items.push({ y: 1e6, draw: () => {
      if (up <= 0) {
        const c = people.step('wake-bed', look, { job: myJob }, bx, by, 0, false, dt, now, k < 0.4 ? 'sleepy' : 'surprised', true);
        c.def.showHeadgear = false;
        ctx.save(); ctx.beginPath(); ctx.rect(bx - 24, by - 60, 48, 60 - 15); ctx.clip(); drawInkPerson(ctx, c, bx, by, 0, 1, now, true); ctx.restore();
        ctx.save(); ctx.translate(bx, by - 15); ctx.scale(1 / 3, 1 / 3);
        I.blob(ctx, [[-44, 0], [44, 0], [46, 40], [-46, 40]], '#e9b8a8', 77, 2.2, 0.8);
        I.line(ctx, [[-40, 12], [40, 12]], 78, 1.4, 'rgba(62,47,40,0.35)'); I.line(ctx, [[-2, 2], [-2, 38]], 79, 1.2, 'rgba(62,47,40,0.25)');
        ctx.restore();
        if (k < 0.4) for (let i = 0; i < 3; i++) { const ph = ((now / 1400) + i / 3) % 1; ctx.globalAlpha = Math.sin(ph * Math.PI); ctx.font = `600 ${5 + i}px ${I.FONT}`; ctx.fillStyle = I.INK; ctx.fillText('z', bx + 8 + ph * 10, by - 26 - ph * 14 - i * 2); ctx.globalAlpha = 1; }
      } else {
        const e = Math.min(1, up), x = bx + (p.x - bx) * e, y = by + (p.y - by) * e, hop = Math.sin(e * Math.PI) * 7;
        const c = people.step(me.id, look, { job: myJob }, x, y, 0, false, dt, now, 'happy');
        drawInkPerson(ctx, c, x, y, hop, 1, now);
        if (k > 0.75) inkBubble(ctx, p.x, p.y - personTop(c) - 2, 'say', 'Mmm… morning already?', (k - 0.75) / 0.25, cy);
      }
    } });
  } else if (pred && me.look) {
    const st = pred.state, face = dlg && dlg.lines[dlg.i]?.[0] === 'p' ? dlg.lines[dlg.i][1] : null;
    items.push({ y: p.y + p.z, draw: () => {
      myTop = drawFolk({ id: me.id, name: me.name, look: me.look, job: myJob, x: p.x, y: p.y, z: p.z, m: st.mount ? (st.flying ? 2 : 1) : 0, kind: st.mount, dir: st.dir, moving: st.moving,
        attacking: st.act === Act.Attack, riders: riders.get(me.id) ?? [], face, fallen: battle.active ? battle.fighter(me.id)?.state === 'down' : fallenUntil > Date.now() }, dt, now);
    } });
  }
  if (shimmerAt && overworld) {   // a hidden grotto close by: just a shimmer in the grass, no name, no pin
    const sx = shimmerAt[0] * TS + 8, sy = shimmerAt[1] * TS + 10;
    items.push({ y: sy - 1, draw: () => { for (let i = 0; i < 7; i++) { const a = now / 900 + i * 0.9, rr = 4 + (i % 3) * 2.5, tw = (Math.sin(now / 260 + i * 2.1) + 1) / 2; ctx.fillStyle = `rgba(255,250,215,${0.25 + tw * 0.6})`; ctx.fillRect(sx + Math.cos(a) * rr, sy - 3 + Math.sin(a) * rr * 0.5 - tw * 3, 1.2, 1.2); } } });
  }
  if (map.id === 'grotto') {   // the grotto's chest: shut until the place is cleared, then glowing
    const [cx2, cy2] = [GROTTO_CHEST[0] * TS + 8, GROTTO_CHEST[1] * TS + 12];
    items.push({ y: cy2, draw: () => {
      if (grottoCleared) { const gl = ctx.createRadialGradient(cx2, cy2 - 4, 1, cx2, cy2 - 4, 18); gl.addColorStop(0, 'rgba(255,236,150,0.7)'); gl.addColorStop(1, 'rgba(255,236,150,0)'); ctx.fillStyle = gl; ctx.fillRect(cx2 - 18, cy2 - 22, 36, 36); }
      ctx.fillStyle = 'rgba(62,47,40,0.2)'; ctx.beginPath(); ctx.ellipse(cx2, cy2 + 1, 8, 2.5, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#9b6a3e'; ctx.fillRect(cx2 - 7, cy2 - 8, 14, 9); ctx.fillStyle = '#c08a52'; ctx.fillRect(cx2 - 7, cy2 - 12, 14, 5);
      ctx.strokeStyle = '#3e2f28'; ctx.lineWidth = 0.8; ctx.strokeRect(cx2 - 7, cy2 - 12, 14, 13); ctx.fillStyle = '#f2c14e'; ctx.fillRect(cx2 - 1.5, cy2 - 8, 3, 3);
    } });
  }
  if (fieldTarget != null && !battle.active) {   // your Tab target out in the world: a gold ring at its feet
    const t = world.remotes.get(fieldTarget), ps = t && !t.goneAt ? world.pose(t) : null;
    if (ps) items.push({ y: ps.y - 0.5, draw: () => { ctx.setLineDash([3, 2]); ctx.lineDashOffset = -now / 70; ctx.lineWidth = 1.1; ctx.strokeStyle = '#f2c14e'; ctx.beginPath(); ctx.ellipse(ps.x, ps.y + 1, 12, 4.5, 0, 0, Math.PI * 2); ctx.stroke(); ctx.setLineDash([]); } });
    else setFieldTarget(null);
  }
  if (gardenView && map.id === 'garden') items.push(...gardenItems(ctx, gardenView, Date.now(), gardenSkew));
  items.sort((a, b) => a.y - b.y);
  for (const it of items) it.draw();
  if (shrineFlare && now - shrineFlare < 1800) drawShrineFlare(now - shrineFlare);
  if (scene) scene.ambient(ctx, t, cx, cy, vw, vh);
  if (battle.active) drawArenaOver(now);
  // sparks and puffs (hits, creatures fading away), in ink units
  if (I.st.parts.length) { ctx.save(); ctx.scale(1 / 3, 1 / 3); for (const q of I.st.parts) I.drawPart(ctx, q); ctx.restore(); }
  // day and night, weather: lights from lamps, windows, moonflowers and every person (lanterns shine further)
  if (sky.darkness() > 0.02 || sky.weather() !== 'clear' || sky.amount('rain') > 0) {
    const lights: Light[] = room ? room.lights.slice() : (scene?.lamps ?? []).slice();
    const personal = (x: number, y: number, look: Look | undefined) => lights.push({ x, y: y - 14, r: look?.wear?.includes('lantern') ? 62 : 24, warm: true });
    if (pred) personal(p.x, p.y, me.look ?? undefined);
    for (const q of remotePoses) personal(q.x, q.y + 16, q.r.info?.look);
    for (const n of npcs) if (n.alpha > 0.3 && onScreen(n.x, n.y)) personal(n.x, n.y, n.look);
    for (const fn of FORAGE) if (fn.when === 'night' && fn.map === map.id && nodeOpen(fn) && (nodeReady.get(fn.id) ?? 0) <= now) lights.push({ x: fn.tile[0] * TS + 8, y: fn.tile[1] * TS + 8, r: 18 });
    sky.draw(ctx, cx, cy, vw, vh, lights, dt, t, !!room);
    if (!room && sky.amount('rain') + sky.amount('storm') < 0.5) drawMoonGlade(ctx, scene, t, cx, cy, vw, vh, Math.max(0, (sky.darkness() - 0.3) / 0.4));   // the moon on the water
  }

  // names, bubbles, the talk hint
  // in a crowd, only the nearest players (and your party) get name tags and speech bubbles, so it stays readable
  const nameMode = settings.s.names;
  const shown = faded.size ? remotePoses.filter(q => !faded.has(q.r.id)) : remotePoses;
  let named = nameMode === 'off' ? [] : shown;
  if (nameMode === 'party') { const mates = party.memberNames(); named = shown.filter(q => mates.has(q.r.info?.name.toLowerCase() ?? '')); }
  else if (nameMode === 'crowd' && shown.length > CROWD_NAMES) {
    const mates = party.memberNames();
    const keep = stableNearest(shown.map(q => ({ id: q.r.id, d: (q.x - p.x) ** 2 + (q.y - p.y) ** 2 })), CROWD_NAMES, 6, nameKeep);
    named = shown.filter(q => keep.has(q.r.id) || mates.has(q.r.info?.name.toLowerCase() ?? ''));
  }
  for (const { r, x, y } of named) if (r.info) nameTag(ctx, x, y, r.info.name, factionById(r.info.faction)?.color ?? null, false);
  for (const c of circles) if (c.can && !c.mine && onScreenC(c.x, c.y)) placeTag(ctx, c.x, c.y - c.r * 0.8 - 6, 'Help wanted! Walk in', null);
  if (pred && !pred.state.seat) nameTag(ctx, p.x, myTop, me.name, factionById(me.faction)?.color ?? null, true);   // riding with someone: their name is enough
  if (overworld && grouped && pred) nameTag(ctx, p.x + 11, p.y - p.z * 0.5 - 2, `+${grouped}`, '#7cc576', false);   // your party, walking as one
  if (battle.active && battle.b?.exit && map.id.startsWith('arena-')) placeTag(ctx, battle.b.exit[0], battle.b.exit[1] - 16, battle.boss ? 'Sealed' : 'Way out', null, false);
  // the overworld: every place you can see gets its name, and how tough it is
  if (overworld) for (const pl of PLACES) {
    if ((pl.world ?? 'world') !== map.id.split('~')[0]) continue;   // only this continent's places
    const x = pl.tile[0] * TS + 8, y = pl.tile[1] * TS + 16;
    if (!onScreenC(x, y)) continue;
    // your airship, moored at the Sky Dock while you're not flying it
    if (pl.id === 'dock' && mountsHost.view?.airship.owned && pred?.state.mount !== AIRSHIP) { drawAirship(ctx, x + 16, y - 2, 10, 1, now, 'back', 0.4); drawAirship(ctx, x + 16, y - 2, 10, 1, now, 'front', 0.4); }
    const lv = pl.id === 'peak' ? maps.bossLabel() : pl.levels;
    placeTag(ctx, x, y + 9, pl.name, lv || null, !!pl.locked, pl.id === 'peak' && !!maps.boss?.open);
  }
  // creatures: a health bar once they're hurt, and a name and level when you're close
  for (const { r, x, y } of mobPoses) {
    const i = r.info!, bf = battle.active ? battle.fighter(r.id) : undefined, hp = bf?.hp ?? i.hp ?? 1, max = bf?.max ?? i.max ?? 1, fx = mobFx.get(r.id);
    if (fx?.deadAt) continue;
    const close = (x - p.x) ** 2 + (y - p.y) ** 2 < 56 * 56 || battle.aim === r.id;
    if (i.mob !== 'shadow' && (close || hp < max)) drawMobLabel(ctx, i.mob, x, y - mobTop(i.mob) - 5, hp, max, close, myJobLv);
    const al = alerts.get(r.id), ak = al ? (now - al) / 1100 : 9;
    if (ak < 1) inkBubble(ctx, x, y - mobTop(i.mob) - 2, 'say', { icon: 'alert' }, ak);
  }
  for (let i = floaters.length - 1; i >= 0; i--) {
    const f = floaters[i], k = (now - f.t0) / 900;
    if (k >= 1) { floaters.splice(i, 1); continue; }
    floatNum(ctx, f.x, f.y, f.text, f.color, k, f.big);
  }
  if (!dlg && settings.s.bubbles) {
    for (const { r, x, y } of (nameMode === 'crowd' ? named : shown.slice(0, 40))) { const b = bubbles.get(r.id); if (b && b.until > now) inkBubble(ctx, x, y, 'say', b.text, (now - b.from) / (b.until - b.from), cy); }
    const mb = bubbles.get(me.id); if (pred && mb && mb.until > now) inkBubble(ctx, p.x, myTop, 'say', mb.text, (now - mb.from) / (mb.until - mb.from), cy);
    for (const n of npcs) if (n.bubble && n.alpha >= 1 && onScreen(n.x, n.y)) inkBubble(ctx, n.x, npcTop.get(n) ?? n.y - 24, n.bubble.kind, n.bubble.content, 1 - (n.bubble.until - now) / (n.bubble.kind === 'say' ? 2800 : 3200), cy);
    if (pred) {
      const n = nearbyNpc(npcs, p.x, p.y, DIR_NAMES[pred.state.dir]);
      const door = nearbyDoor(p.x, p.y);
      if (n) inkHint(ctx, n.x, (npcTop.get(n) ?? n.y - 24) - 8, now);
      else if (door) { const [dx, dy] = doorPoint(door, TS), push = pushOf(door); const shut = !shopOpen(door.to, sky.hour()); placeTag(ctx, Math.min(MW * TS - 30, Math.max(30, dx)), Math.max(cy + 14, dy + (push === 'up' ? 2 : -12)), shut ? `${door.label} · Closed` : door.label, shut ? closedText(door.to).replace('Closed for the night. ', '') : null, shut); }
      else { const fn = nearbyNode(p.x, p.y), sh = fn ? null : nearbyShrine(p.x, p.y);
        if (fn) inkHint(ctx, fn.tile[0] * TS + 8, fn.tile[1] * TS - 4, now);
        else if (sh) { const here = myBind === map.id.split('~')[0]; placeTag(ctx, sh.x, sh.y - 40, here ? 'Your wayshrine ✓' : 'E · Bind here', here ? 'you wake here after fainting' : 'wake here if you faint', here); }
      }
    }
  }
  updateNpcs(npcs, now, p.x, p.y, dlg?.npc ?? null);
  if (critters.length) stepCritters(critters, dt, now, map, p.x, p.y, sky.darkness() > 0.35);
  walkNpcs(npcs, dt, now, map, p.x, p.y, dlg?.npc ?? null);
  if (film && film.fade > 0.01) { ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.fillStyle = `rgba(30,24,20,${film.fade})`; ctx.fillRect(0, 0, cv.width, cv.height); }   // between shots
  if (wake && now - wake.t0 < 700) { ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.fillStyle = `rgba(30,24,20,${1 - (now - wake.t0) / 700})`; ctx.fillRect(0, 0, cv.width, cv.height); }   // eyes opening

  // screen space
  ctx.setTransform(sc, 0, 0, sc, 0, 0); ctx.imageSmoothingEnabled = true;
  if (dlg && me.look) {
    const line = dlg.lines[dlg.i];
    const shownBefore = dlg.shown;
    if (!dlg.skip) dlg.shown = Math.min(line[2].length, Math.floor((now - dlg.t0) / 28));
    if (Math.floor(dlg.shown / 3) > Math.floor(shownBefore / 3) && line[2][dlg.shown - 1] !== ' ') audio.play('blip', undefined, line[0] === 'p' ? 1.25 : 0.85 + (npcs.indexOf(dlg.npc) % 4) * 0.08);
    const mine = line[0] === 'p';
    if (pred) dlg.npc.dir = dirToward(p.x - dlg.npc.x, p.y - dlg.npc.y);
    const who = mine ? portraits.step('me', me.look, { job: myJob }, 0, 0, 0, false, dt, now, line[1], true)
      : portraits.step(`n${npcs.indexOf(dlg.npc)}`, dlg.npc.look, { name: dlg.npc.name, npc: true }, 0, 0, 0, false, dt, now, line[1], true);
    inkDialogue(ctx, vw, vh - Math.ceil(padInset * S / sc), {
      name: mine ? me.name : dlg.npc.name, right: mine, who,
      tagColor: mine ? (factionById(me.faction)?.color ?? '#fff') : '#e6dcc4',
      text: line[2].slice(0, dlg.shown), done: dlg.shown >= line[2].length,
    }, now);
  }
}

function updateDebug(): void {
  if (!conn || !pred) { debug.set(null); return; }
  conn.tickStats();
  debug.set([
    `transport  ${conn.kind} · ${conn.codec.name}`,
    conn.transportNote ? `           ${conn.transportNote}` : '',
    `ping       ${conn.rtt.toFixed(0)} ms  ±${conn.jitter.toFixed(0)}`,
    `snapshots  ${world.snapsPerSec.toFixed(0)}/s  delay ${(world.delayTicks * TICK_MS).toFixed(0)} ms`,
    `corrected  ${pred.corrections} (snapped ${pred.snaps}, last ${pred.lastError.toFixed(1)} px)`,
    `unacked    ${pred.unacked} inputs`,
    `players    ${world.remotes.size + 1} in view · layer ${layer}`,
    `bandwidth  ↓${conn.kbIn.toFixed(1)} ↑${conn.kbOut.toFixed(1)} KB/s`,
    `fps        ${fps.toFixed(0)}  sprites ${sprites.built}`,
  ].filter(Boolean));
}


// Read-only hook for automated browser tests and console debugging.
(window as unknown as { __tt: unknown }).__tt = {
  get me() { return me; }, get pred() { return pred; }, world, get conn() { return conn; }, get npcs() { return npcs; },
  get dlg() { return dlg; }, drawPerson: drawInkPerson, input, vessels: () => vesselsAt(Date.now()), sprites, audio, people, settings, renderPreview, get zoneId() { return map.id; },
  /** Open a conversation with a townsperson (for UI checks on different screens). */
  talk(i = 0) { const p = myPos(); beginTalk(npcs[i], p.x, p.y, performance.now()); },
};
